import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionIcon, EyeIcon, PencilIcon, TrashIcon, SendIcon } from './ActionIcons';
import { SortTh, useSortState, sortRows } from './SortableHeader';
import { Card, RNLLoader, Input, Select, SearchableSelect, Modal, useToast, Btn, StatusReason, ReturnDestinationNote } from '../UI';
import * as api from '../../api';
import { fmtNum, fmtDate, belongsInMyDrafts, isDepartmentBounce, offersConsiderationStatus, considerationOptionsFor, defaultConsiderationFor, isAwaitingCorrection, correctionTarget } from '../../utils';

import PRDetailsView from './PRDetailsView';
import RequestedLineItems from './RequestedLineItems';
import { PinnedDetail, useScrollViewport } from './PinnedDetail';
import Pagination from './Pagination';
import NewRequisition from './NewRequisition';
import { vatCell } from './RequisitionLineItemsTable';

// ─── tiny helpers ────────────────────────────────────────────────────────────
// Maps a consideration status's configured colour token to a hex accent for
// the group header bar. Unknown values fall back to grey rather than breaking,
// since the colours are admin-editable free text.
const GROUP_COLOR = {
  blue: '#2563eb', green: '#16a34a', amber: '#d97706', red: '#dc2626',
  purple: '#7c3aed', cyan: '#0891b2', indigo: '#4f46e5', gray: '#64748b',
};

const fmt = (v) =>
  v != null && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '—';

// Plain native dropdowns for the CC review grid — no popup modal, fixed width
// so a long selected label truncates in place instead of growing the column.
const ccSelectStyle = {
  width: 130, maxWidth: 130, boxSizing: 'border-box', padding: '4px 6px',
  border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11.5, background: '#f8fafc',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const ccSelectDisabledStyle = { ...ccSelectStyle, background: '#e2e8f0', color: '#94a3b8', cursor: 'not-allowed' };

export default function MyRequisitions({
  queueStep, title, departmentId, draftsOnly, onEditingChange,
  // Cost Control's workspace, lifted so it can live in the URL. Supplied by
  // the requisition page from the path; left undefined everywhere else, where
  // the component keeps its own state exactly as before.
  ccView: ccViewProp, onCcViewChange,
  // Which requisition is open, and whether for editing. Same controlled /
  // uncontrolled contract as ccView above.
  openPrId, onOpenPr, openForEdit,
}) {
  const [loading, setLoading] = useState(true);
  // Server-side paging. 10 to start: these lists run to hundreds of rows and
  // fetching them whole is what made the tab slow to open.
  const [page, setPage] = useState({ skip: 0, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);
  // Caps the expanded row at the visible width so its tables scroll on
  // their own instead of dragging the whole panel sideways.
  const viewport = useScrollViewport([loading]);
  const [prs, setPrs] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [searchVersion, setSearchVersion] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [requesterFilter, setRequesterFilter] = useState('');

  const [viewingPrIdState, setViewingPrIdState] = useState(null);
  const [viewingInstanceId, setViewingInstanceId] = useState(null);
  const [editingPrIdState, setEditingPrIdState] = useState(null);

  // One address carries both: /<prId> opens the detail, /<prId>/edit opens the
  // correction editor. Aliased so every reference below is unchanged.
  const viewingPrId = openPrId !== undefined ? (openForEdit ? null : openPrId) : viewingPrIdState;
  const editingPrId = openPrId !== undefined ? (openForEdit ? openPrId : null) : editingPrIdState;
  const setViewingPrId = onOpenPr ? ((id) => onOpenPr(id, false)) : setViewingPrIdState;
  const setEditingPrId = onOpenPr ? ((id) => onOpenPr(id, true)) : setEditingPrIdState;
  // Correcting a returned requisition takes over the whole screen, so the
  // parent is told to drop its department-hub chrome. Without it the editor
  // rendered underneath a "Back to Department Hub" bar and a tab row that
  // belong to the page it was launched from, giving two competing back
  // buttons and a nav that would abandon the correction.
  useEffect(() => {
    if (onEditingChange) onEditingChange(!!editingPrId);
  }, [editingPrId, onEditingChange]);

  const toast = useToast();
  const [submittingPrId, setSubmittingPrId] = useState(null);
  // In-flight flag for the draft submission, so the dialog can show
  // progress and refuse a second click while the first is still running.
  const [submittingWorkflow, setSubmittingWorkflow] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [selectedApproverId, setSelectedApproverId] = useState('');
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [showSubmitSuccessModal, setShowSubmitSuccessModal] = useState(false);
  const [submittedPRNumber, setSubmittedPRNumber] = useState('');

  const [deletingPrId, setDeletingPrId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ── CC Queue inline state ──────────────────────────────────────────────────
  // lineEdits[prId][itemId] = { cc_qty, cc_rate, cc_amount, cc_gl, cc_approved, cc_rejected, cc_rejected_reason, project_id, boq_item_id, boq_component_id }
  // There's no separate "save" step anymore — whatever's here is saved together
  // with whichever workflow action button is clicked (see submitAction).
  const [lineEdits, setLineEdits] = useState({});
  const [ccHeaderEdits, setCcHeaderEdits] = useState({});
  const [ccHeaderError, setCcHeaderError] = useState(null);
  // lineFigureErrors[prId][itemId] = { qty: true, rate: true }
  // These figures are what the payment voucher loader sums; a line approved
  // without them saves approved_amount NULL, so the requisition reaches
  // APPROVED but can never be loaded into a voucher.
  const [lineFigureErrors, setLineFigureErrors] = useState({});

  // Cost Control can reassign a line's Project / BOQ / Sub-BOQ as part of their
  // review — needs the project list + cascading BOQ data, same as the forms.
  const [projects, setProjects] = useState([]);
  const [boqDataByProject, setBoqDataByProject] = useState({});

  // per-PR workflow action state
  const [actionModal, setActionModal] = useState(null); // { prId, instanceId, type: 'approve'|'reject' }
  const [actionComment, setActionComment] = useState('');
  const [nextStepInfo, setNextStepInfo] = useState(null);
  // Return destination and the reason the request is being parked. This is the
  // Cost Control / Approval queue's own action modal — a third one alongside
  // PRDetailsView's and MyInbox's — so it needs the same two pickers.
  const [returnTargets, setReturnTargets] = useState([]);
  const [returnTarget, setReturnTarget] = useState('');
  // The step doing the returning — decides whether a consideration
  // status is on offer at all. See offersConsiderationStatus.
  const [returnFromStep, setReturnFromStep] = useState('');
  const [considerationStatuses, setConsiderationStatuses] = useState([]);
  const [considerationStatusId, setConsiderationStatusId] = useState('');
  const [nextApproverId, setNextApproverId] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);
  // `${prId}:${type}` for the row whose dialog is being prepared, so only the
  // button that was pressed shows the wait.
  const [preparingAction, setPreparingAction] = useState(null);
  const [rejectReasonModal, setRejectReasonModal] = useState(null); // { prId, itemId }
  const [firstStepInfo, setFirstStepInfo] = useState(null);

  const navigate = useNavigate();
  // Column sort. State only — the rows are sorted further down,
  // after they are projected and below this component's early returns.
  const sortState = useSortState();
  const isCCQueue = queueStep === 'cc-queue';
  const isApprovalQueue = queueStep === 'approval-queue';

  // ── CC Queue workspace switcher (Load / Reviewed / Pending Consideration) ──
  // null | 'load' | 'reviewed' | 'pending' | 'rejected'
  //
  // Controlled when the caller passes ccView, uncontrolled otherwise. Aliasing
  // both to the same names means every reference below is untouched — the
  // component cannot tell which mode it is in, so there is no second code path
  // to keep in step.
  const [ccViewState, setCcViewState] = useState(null);
  const ccView = ccViewProp !== undefined ? ccViewProp : ccViewState;
  const setCcView = onCcViewChange || setCcViewState;

  // Any change of what is being listed starts at page one: staying on page 4
  // while the list underneath changes lands the reader somewhere arbitrary.
  useEffect(() => {
    setPage((p) => (p.skip === 0 ? p : { ...p, skip: 0 }));
  }, [queueStep, ccView, draftsOnly, appliedSearch]);
  const [ccCounts, setCcCounts] = useState({ load: null, reviewed: null, pending: null, rejected: null });
  // Pending Consideration is grouped server-side, so it does not go through
  // the flat `prs` list the other two workspaces share.
  const [considerationGroups, setConsiderationGroups] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const isCCLoadView = isCCQueue && ccView === 'load';
  // Cost Control now edits in two places: Load Requisitions, and Pending
  // Consideration — where a request the Approval Queue sent back lands. Both
  // need the same review grid, GL field and actions, so every check that
  // meant "CC is working on this" reads this rather than the load view alone.
  const isCCEditing = isCCQueue && (ccView === 'load' || ccView === 'pending');
  const isCCReviewedView = isCCQueue && ccView === 'reviewed';
  const isCCPendingView = isCCQueue && ccView === 'pending';
  const isCCRejectedView = isCCQueue && ccView === 'rejected';

  // ── Approval Queue inline state ────────────────────────────────────────────
  const [apprEdits, setApprEdits] = useState({});

  // ── Outcome modal (approve/reject success) ─────────────────────────────────
  const [outcomeModal, setOutcomeModal] = useState(null); // { type, title, message, prNumber }

  // Clearing this when the destination stops being Cost Control: the picker
  // hides, but without this the previously chosen value would still be sent
  // and would park the request under a heading nobody will look at.
  useEffect(() => {
    if (!offersConsiderationStatus(returnFromStep, returnTargets, returnTarget)) {
      setConsiderationStatusId('');
      return;
    }
    // Picking a destination pre-selects the heading that matches it — the
    // requester pairs with "Returned To Requester", the department head with
    // "Returned To Department Head". A starting point only: the approver can
    // still choose any other heading afterwards.
    const label = (returnTargets || []).find((t) => t.value === returnTarget)?.label;
    const suggested = defaultConsiderationFor(label, considerationStatuses);
    if (suggested) setConsiderationStatusId(suggested);
  }, [returnTarget, returnTargets, returnFromStep, considerationStatuses]);

  const fetchPRs = useCallback(async () => {
    try {
      api.clearApiCache();
      if (isCCQueue && !ccView) {
        // No workspace selected yet — wait for the user to pick one.
        setPrs([]);
        setLoading(false);
        return;
      }

      let res;
      if (isCCPendingView) {
        const grouped = await api.getPendingConsideration();
        setConsiderationGroups(grouped || { total: 0, total_value: 0, groups: [] });
        // The other two workspaces derive their badge from the row count they
        // just loaded; this one is grouped, so the total comes from the payload.
        setCcCounts((prev) => ({ ...prev, pending: grouped?.total || 0 }));
        // The first group starts expanded; opening everything at once on a
        // busy queue would bury the headings the panel exists to show.
        const first = (grouped?.groups || [])[0];
        setOpenGroups((prev) =>
          Object.keys(prev).length ? prev : (first ? { [first.status_name]: true } : {}));
        // Flattened into the normal list so everything below — line edits,
        // the project/BOQ prefetch, the review grid — works here exactly as it
        // does in Load Requisitions. Cost Control has real work to do on these.
        res = (grouped?.groups || []).flatMap((g) => g.requests || []);
      } else {
        const { skip, limit } = page;
        res = isCCReviewedView
          ? await api.getPrsByQueueReviewed(queueStep, skip, limit)
          // 'rejected' only: work Cost Control returned is tracked in Pending
          // Consideration, and listing it here too would count it twice.
          : isCCRejectedView
          ? await api.getPrsRejectedReturnedByQueue(queueStep, 'rejected', skip, limit)
          : queueStep
          ? await api.getPrsByQueue(queueStep, skip, limit, appliedSearch)
          : await api.listMyPRs(skip, limit, appliedSearch);
      }
      const rawList = Array.isArray(res) ? res : (res?.data || []);
      // Set by api.js from the X-Total-Count header. Pending Consideration is
      // grouped rather than paged, so it falls back to what it actually has.
      setTotalCount(
        res?.totalCount != null ? res.totalCount : rawList.length);
      // Defensive de-dupe: a resubmitted PR can be returned more than once by
      // the backend history query, which breaks React's row keys.
      const seenIds = new Set();
      const list = rawList.filter((pr) => {
        const id = pr.id || pr.purchase_request_id;
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      });
      setPrs(list);
      if (isCCQueue) {
        setCcCounts((prev) => ({ ...prev, [ccView]: list.length }));
      }

      // Initialise lineEdits from existing item data
      if (isCCQueue) {
        const edits = {};
        list.forEach((pr) => {
          const prId = pr.id || pr.purchase_request_id;
          edits[prId] = {};
          (pr.items || []).forEach((item) => {
            edits[prId][item.item_id] = {
              cc_qty: item.cost_control_quantity ?? '',
              cc_rate: item.cost_control_unit_rate ?? '',
              cc_amount: item.cost_control_amount ?? '',
              cc_gl: item.cost_control_general_ledger ?? '',
              cc_approved: item.cost_control_approved ?? false,
              cc_rejected: item.cost_control_rejected ?? false,
              cc_rejected_reason: item.cost_control_rejected_reason ?? '',
              project_id: item.project_id ?? '',
              boq_item_id: item.boq_item_id ?? '',
              boq_component_id: item.boq_component_id ?? '',
            };
          });
        });
        setLineEdits(edits);

        if (isCCEditing) {
          if (projects.length === 0) {
            api.listProjects().then((res) => {
              setProjects(Array.isArray(res) ? res : (res?.data || []));
            }).catch(() => {});
          }
          const projectIds = Array.from(new Set(
            list.flatMap((pr) => (pr.items || []).map((it) => it.project_id)).filter(Boolean)
          ));
          projectIds.forEach((pid) => {
            if (boqDataByProject[pid]) return;
            api.listBOQ(pid)
              .then((res) => {
                const boqItems = Array.isArray(res) ? res : (res?.data || res?.items || []);
                setBoqDataByProject((prev) => ({ ...prev, [pid]: boqItems }));
              })
              .catch(() => {});
          });
        }
      }
      if (isApprovalQueue) {
        const edits = {};
        list.forEach((pr) => {
          const prId = pr.id || pr.purchase_request_id;
          edits[prId] = {};
          (pr.items || []).forEach((item) => {
            edits[prId][item.item_id] = {
              appr_qty: item.approved_quantity ?? '',
              appr_rate: item.approved_unit_rate ?? '',
              appr_amount: item.approved_amount ?? '',
              appr_approved: item.approved ?? false,
              appr_on_hold: item.on_hold ?? false,
              appr_rejected: item.rejected ?? false,
              appr_reason: item.rejected_reason ?? '',
            };
          });
        });
        setApprEdits(edits);
      }
    } catch (e) {
      console.error('Failed to fetch PRs', e);
      setPrs([]);
    } finally {
      setLoading(false);
    }
  }, [queueStep, isCCQueue, isApprovalQueue, isCCReviewedView, isCCPendingView, ccView, page, appliedSearch, searchVersion]);

  useEffect(() => {
    setLoading(true);
    fetchPRs();
  }, [fetchPRs]);

  // Eagerly load the badge counts for every CC Queue workspace card up front,
  // independent of which one the user has actually opened. `countsInitializing`
  // keeps the page-level loader up until these resolve too, so the spinner
  // doesn't vanish the instant the (near-instant) main list fetch finishes
  // while the counts are still quietly loading in the background.
  const [countsInitializing, setCountsInitializing] = useState(true);

  const dedupedLength = (rawList) => {
    const seenIds = new Set();
    return rawList.filter((pr) => {
      const id = pr.id || pr.purchase_request_id;
      if (seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    }).length;
  };

  // Silent re-fetch of both workspace counts — used after an action moves a
  // PR between workspaces, so the badges don't keep showing whatever they
  // were computed as on the initial mount. Unlike the mount-time load below,
  // this never toggles countsInitializing, so it can't yank the page back to
  // a full-screen loader out from under an open outcome modal.
  const refreshCcCounts = () => {
    if (!isCCQueue) return;
    api.clearApiCache();
    api.getPrsByQueue(queueStep)
      .then((res) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        setCcCounts((prev) => ({ ...prev, load: dedupedLength(list) }));
      })
      .catch(() => {});
    api.getPrsByQueueReviewed(queueStep)
      .then((res) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        setCcCounts((prev) => ({ ...prev, reviewed: dedupedLength(list) }));
      })
      .catch(() => {});
    // Pending Consideration was missing here, so its badge only ever appeared
    // after the workspace had been opened — and once shown it went stale,
    // since nothing recomputed it when a request was parked or released.
    // It is grouped server-side, so the count comes from the payload total
    // rather than a row count.
    api.getPendingConsideration()
      .then((res) => setCcCounts((prev) => ({ ...prev, pending: res?.total || 0 })))
      .catch(() => {});
    // Rejections change this card, so it refreshes with the rest — otherwise
    // the badge would keep the value it had when the page first loaded.
    api.getPrsRejectedReturnedByQueue(queueStep, 'rejected')
      .then((res) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        setCcCounts((prev) => ({ ...prev, rejected: dedupedLength(list) }));
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!isCCQueue) {
      setCountsInitializing(false);
      return;
    }
    setCountsInitializing(true);
    Promise.all([
      api.getPrsByQueue(queueStep)
        .then((res) => {
          const list = Array.isArray(res) ? res : (res?.data || []);
          setCcCounts((prev) => (prev.load == null ? { ...prev, load: dedupedLength(list) } : prev));
        })
        .catch(() => {}),
      api.getPrsByQueueReviewed(queueStep)
        .then((res) => {
          const list = Array.isArray(res) ? res : (res?.data || []);
          setCcCounts((prev) => (prev.reviewed == null ? { ...prev, reviewed: dedupedLength(list) } : prev));
        })
        .catch(() => {}),
      // Fetched up front like the other two, so all three cards carry a count
      // on first paint instead of Workspace 3 filling in only once opened.
      api.getPendingConsideration()
        .then((res) => {
          setCcCounts((prev) => (prev.pending == null ? { ...prev, pending: res?.total || 0 } : prev));
        })
        .catch(() => {}),
      // Fetched up front with the other three, so Workspace 4 carries a count
      // on first paint rather than spinning until it is opened.
      api.getPrsRejectedReturnedByQueue(queueStep, 'rejected')
        .then((res) => {
          const list = Array.isArray(res) ? res : (res?.data || []);
          setCcCounts((prev) => (prev.rejected == null ? { ...prev, rejected: dedupedLength(list) } : prev));
        })
        .catch(() => {}),
    ]).finally(() => setCountsInitializing(false));
  }, [isCCQueue, queueStep]);

  // ─── CC line edit helpers ──────────────────────────────────────────────────
  // Drop a line's red flag the moment the missing figure is supplied.
  const clearFigureError = (prId, itemId, field, value) => {
    if (value === '' || value === null || value === undefined) return;
    const key = (field === 'cc_qty' || field === 'appr_qty') ? 'qty'
      : (field === 'cc_rate' || field === 'appr_rate') ? 'rate' : null;
    if (!key) return;
    setLineFigureErrors((prev) => {
      if (!prev[prId]?.[itemId]?.[key]) return prev;
      const rows = { ...prev[prId], [itemId]: { ...prev[prId][itemId] } };
      delete rows[itemId][key];
      if (!Object.keys(rows[itemId]).length) delete rows[itemId];
      return { ...prev, [prId]: rows };
    });
  };

  const updateLineEdit = (prId, itemId, field, value) => {
    clearFigureError(prId, itemId, field, value);
    setLineEdits((prev) => {
      const prEdits = { ...(prev[prId] || {}) };
      const item = { ...(prEdits[itemId] || {}) };
      item[field] = value;
      // Auto-calc amount
      if (field === 'cc_qty' || field === 'cc_rate') {
        const q = parseFloat(field === 'cc_qty' ? value : item.cc_qty);
        const r = parseFloat(field === 'cc_rate' ? value : item.cc_rate);
        if (!isNaN(q) && !isNaN(r)) item.cc_amount = (q * r).toFixed(2);
      }
      prEdits[itemId] = item;
      return { ...prev, [prId]: prEdits };
    });
  };

  const setCCStatus = (prId, itemId, status) => {
    setLineEdits((prev) => {
      const prEdits = { ...(prev[prId] || {}) };
      const item = { ...(prEdits[itemId] || {}) };
      item.cc_approved = status === 'approved';
      item.cc_rejected = status === 'rejected';
      prEdits[itemId] = item;
      return { ...prev, [prId]: prEdits };
    });
    if (status === 'rejected') {
      setRejectReasonModal({ prId, itemId });
    }
  };

  // Cost Control reassigning a line's Project / BOQ / Sub-BOQ — changing
  // Project clears the BOQ choices below it; BOQ data is fetched lazily per project.
  const updateItemProjectOrBoq = (prId, itemId, field, value) => {
    setLineEdits((prev) => {
      const prEdits = { ...(prev[prId] || {}) };
      const item = { ...(prEdits[itemId] || {}), [field]: value };
      if (field === 'project_id') { item.boq_item_id = ''; item.boq_component_id = ''; }
      if (field === 'boq_item_id') { item.boq_component_id = ''; }
      prEdits[itemId] = item;
      return { ...prev, [prId]: prEdits };
    });
    if (field === 'project_id' && value && !boqDataByProject[value]) {
      api.listBOQ(value)
        .then((res) => {
          const boqItems = Array.isArray(res) ? res : (res?.data || res?.items || []);
          setBoqDataByProject((prev) => ({ ...prev, [value]: boqItems }));
        })
        .catch(() => {});
    }
  };

  // ─── Approval line edit helpers ────────────────────────────────────────────
  const updateApprEdit = (prId, itemId, field, value) => {
    clearFigureError(prId, itemId, field, value);
    setApprEdits((prev) => {
      const prEdits = { ...(prev[prId] || {}) };
      const item = { ...(prEdits[itemId] || {}) };
      item[field] = value;
      if (field === 'appr_qty' || field === 'appr_rate') {
        const q = parseFloat(field === 'appr_qty' ? value : item.appr_qty);
        const r = parseFloat(field === 'appr_rate' ? value : item.appr_rate);
        if (!isNaN(q) && !isNaN(r)) item.appr_amount = (q * r).toFixed(2);
      }
      prEdits[itemId] = item;
      return { ...prev, [prId]: prEdits };
    });
  };

  // A figure copied out of the API carries the database's scale with it —
  // quantity is Numeric(14,3), so the server sends "10.000" where the approver
  // expects to see 10.00.
  //
  // Shown to two decimals, but NOT with a blind toFixed(2): that column really
  // does hold three decimals, and rounding here would turn a genuine quantity
  // of 0.125 into 0.13. Two decimals are used only where they represent the
  // value exactly; anything finer is left alone.
  const cleanFigure = (v) => {
    if (v == null || v === '') return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return v;
    return n === Number(n.toFixed(2)) ? n.toFixed(2) : String(n);
  };

  // Bring Cost Control's figures across into the approver's own columns, for
  // every line at once. Cost Control has already priced the request, so on the
  // common path the approver is agreeing with it — retyping the same numbers
  // line by line was the whole cost of that agreement.
  //
  // A starting point, not a decision: nothing is submitted here, and each line
  // stays individually editable afterwards. The per-line Approve / Hold /
  // Reject marks are deliberately untouched — copying figures is not the same
  // as accepting a line, and this must not silently approve anything.
  const copyCCToApproval = (pr) => {
    const prId = pr.id || pr.purchase_request_id;

    // Worked out HERE, before any state is touched. Counting inside the
    // setApprEdits updater looked equivalent and was not: React runs that
    // updater later (and twice under StrictMode), so the count was still zero
    // when the toast read it — the copy happened while the toast reported
    // that nothing had been copied.
    //
    // A line Cost Control never priced has nothing to copy; it is skipped
    // rather than filled with zeros the approver would have to clear.
    const copyable = (pr.items || []).filter(
      (item) => item.cost_control_quantity != null || item.cost_control_unit_rate != null,
    );

    if (!copyable.length) {
      toast.show('Cost Control has not entered any figures on this request yet.', 'info');
      return;
    }

    setApprEdits((prev) => {
      const prEdits = { ...(prev[prId] || {}) };
      copyable.forEach((item) => {
        const qty = item.cost_control_quantity;
        const rate = item.cost_control_unit_rate;
        const amount = item.cost_control_amount;
        prEdits[item.item_id] = {
          ...(prEdits[item.item_id] || {}),
          appr_qty: cleanFigure(qty),
          appr_rate: cleanFigure(rate),
          // Prefer CC's own total; fall back to qty x rate so the column is
          // never left disagreeing with the two figures beside it.
          appr_amount: amount != null
            ? cleanFigure(amount)
            : (qty != null && rate != null ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : ''),
        };
      });
      return { ...prev, [prId]: prEdits };
    });

    toast.show(
      `Cost Control figures copied into ${copyable.length} line(s). `
      + 'Adjust any of them before you approve.',
      'success',
    );
  };

  // The approver may prefer the requester's own figures to Cost Control's: the same copy, from the requested quantity,
  // estimated rate and amount. Again a starting point — no line is approved by it.
  const copyRequestToApproval = (pr) => {
    const prId = pr.id || pr.purchase_request_id;
    // counted before state is touched (see copyCCToApproval)
    const copyable = (pr.items || []).filter(
      (item) => item.quantity != null || item.estimated_unit_rate != null,
    );

    if (!copyable.length) {
      toast.show('This requisition has no figures to copy.', 'info');
      return;
    }

    setApprEdits((prev) => {
      const prEdits = { ...(prev[prId] || {}) };
      copyable.forEach((item) => {
        const qty = item.quantity;
        const rate = item.estimated_unit_rate;
        const amount = item.estimated_amount;
        prEdits[item.item_id] = {
          ...(prEdits[item.item_id] || {}),
          appr_qty: cleanFigure(qty),
          appr_rate: cleanFigure(rate),
          appr_amount: amount != null
            ? cleanFigure(amount)
            : (qty != null && rate != null ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : ''),
        };
      });
      return { ...prev, [prId]: prEdits };
    });

    toast.show(
      `Requested figures copied into ${copyable.length} line(s). `
      + 'Adjust any of them before you approve.',
      'success',
    );
  };

  // The same idea one step earlier in the chain: bring the requester's own
  // figures across into Cost Control's columns for every line at once. Cost
  // Control usually agrees with what was asked for and only adjusts the odd
  // line, so retyping the identical numbers was the whole cost of agreeing.
  //
  // A starting point, not a decision. Nothing is submitted, every line stays
  // editable, and the per-line approve/reject marks are deliberately left
  // alone — copying figures is not the same as accepting a line.
  const copyRequestToCC = (pr) => {
    const prId = pr.id || pr.purchase_request_id;

    // Counted BEFORE state is touched, for the same reason as the approval
    // copy: React runs the updater later, so a count taken inside it is still
    // zero when the toast reads it.
    //
    // quantity is NOT NULL on a line, so a line with no rate still has
    // something worth copying — the rate simply comes across empty.
    const copyable = (pr.items || []).filter(
      (item) => item.quantity != null || item.estimated_unit_rate != null,
    );

    if (!copyable.length) {
      toast.show('This requisition has no figures to copy.', 'info');
      return;
    }

    setLineEdits((prev) => {
      const prEdits = { ...(prev[prId] || {}) };
      copyable.forEach((item) => {
        const qty = item.quantity;
        const rate = item.estimated_unit_rate;
        const amount = item.estimated_amount;
        prEdits[item.item_id] = {
          ...(prEdits[item.item_id] || {}),
          cc_qty: cleanFigure(qty),
          cc_rate: cleanFigure(rate),
          // Prefer the line's own total; fall back to qty x rate so the
          // amount never disagrees with the two figures beside it.
          cc_amount: amount != null
            ? cleanFigure(amount)
            : (qty != null && rate != null ? (parseFloat(qty) * parseFloat(rate)).toFixed(2) : ''),
        };
      });
      return { ...prev, [prId]: prEdits };
    });

    toast.show(
      `Requested figures copied into ${copyable.length} line(s). `
      + 'Adjust any of them before you approve.',
      'success',
    );
  };

  const setApprStatus = (prId, itemId, status) => {
    setApprEdits((prev) => {
      const prEdits = { ...(prev[prId] || {}) };
      const item = { ...(prEdits[itemId] || {}) };
      item.appr_approved = status === 'approved';
      item.appr_on_hold = status === 'on_hold';
      item.appr_rejected = status === 'rejected';
      prEdits[itemId] = item;
      return { ...prev, [prId]: prEdits };
    });
    // Open reason modal for both hold and reject
    if (status === 'on_hold' || status === 'rejected') {
      setRejectReasonModal({ prId, itemId, mode: 'approval', status });
    }
  };

  // True once at least one line has an explicit decision — used to detect a
  // skipped review so submitAction can auto-mark every line instead.
  const hasAnyReview = (pr, isCC) => {
    const prId = pr.id || pr.purchase_request_id;
    const prEdits = (isCC ? lineEdits : apprEdits)[prId] || {};
    return (pr.items || []).some((item) => {
      const e = prEdits[item.item_id] || {};
      return isCC ? (e.cc_approved || e.cc_rejected) : (e.appr_approved || e.appr_on_hold || e.appr_rejected);
    });
  };

  // Builds the CC/Approval item-review payload from whatever's currently in
  // lineEdits/apprEdits. When autoFillStatus is set (no manual review was
  // done), every line is stamped with that status instead, and the modal's
  // general comment becomes each line's rejected/hold reason.
  // "Same GL for every line" — on by default, because one ledger for the whole
  // requisition is the ordinary case and typing it once should be enough.
  // Untick it when a request spans accounts (parts, fuel, workmanship) and each
  // line gets its own code.
  //
  // Per requisition, not global: two requisitions open in the same session can
  // legitimately want different answers.
  const [glAllLines, setGlAllLines] = useState({});
  // Off by default. Lines commonly post to different accounts — parts, fuel,
  // workmanship — so the per-line boxes start editable and the header code is
  // opt-in rather than something to undo.
  const glAppliesToAll = (prId) => glAllLines[prId] === true;

  // Push the header code onto every line. Called when the header GL is typed
  // and when the switch is turned back on, so what the reviewer sees in the
  // column is always what will actually be saved — the alternative is a grid
  // showing one thing and the server storing another.
  const applyHeaderGlToLines = (pr, value) => {
    const prId = pr.id || pr.purchase_request_id;
    setLineEdits((prev) => {
      const prEdits = { ...(prev[prId] || {}) };
      (pr.items || []).forEach((item) => {
        prEdits[item.item_id] = { ...(prEdits[item.item_id] || {}), cc_gl: value };
      });
      return { ...prev, [prId]: prEdits };
    });
  };

  const buildCCReviewItems = (pr, autoFillStatus) => {
    const prId = pr.id || pr.purchase_request_id;
    const prEdits = lineEdits[prId] || {};
    return (pr.items || []).map((item) => {
      const e = prEdits[item.item_id] || {};
      return {
        item_id: item.item_id,
        cost_control_quantity: e.cc_qty !== '' ? parseFloat(e.cc_qty) : null,
        cost_control_unit_rate: e.cc_rate !== '' ? parseFloat(e.cc_rate) : null,
        cost_control_amount: e.cc_amount !== '' ? parseFloat(e.cc_amount) : null,
        cost_control_general_ledger: (e.cc_gl ?? '').trim() || null,
        cost_control_approved: autoFillStatus ? autoFillStatus === 'approved' : (e.cc_approved || false),
        cost_control_rejected: autoFillStatus ? autoFillStatus === 'rejected' : (e.cc_rejected || false),
        cost_control_rejected_reason: autoFillStatus === 'rejected' ? actionComment : (e.cc_rejected_reason || null),
        project_id: e.project_id || item.project_id,
        boq_item_id: e.boq_item_id || null,
        boq_component_id: e.boq_component_id || null,
      };
    });
  };

  const buildApprReviewItems = (pr, autoFillStatus) => {
    const prId = pr.id || pr.purchase_request_id;
    const prEdits = apprEdits[prId] || {};
    return (pr.items || []).map((item) => {
      const e = prEdits[item.item_id] || {};
      return {
        item_id: item.item_id,
        approved_quantity: e.appr_qty !== '' ? parseFloat(e.appr_qty) : null,
        approved_unit_rate: e.appr_rate !== '' ? parseFloat(e.appr_rate) : null,
        approved_amount: e.appr_amount !== '' ? parseFloat(e.appr_amount) : null,
        approved: autoFillStatus ? autoFillStatus === 'approved' : (e.appr_approved || false),
        on_hold: autoFillStatus ? autoFillStatus === 'on_hold' : (e.appr_on_hold || false),
        rejected: autoFillStatus ? autoFillStatus === 'rejected' : (e.appr_rejected || false),
        rejected_reason: (autoFillStatus === 'rejected' || autoFillStatus === 'on_hold') ? actionComment : (e.appr_reason || null),
      };
    });
  };

  // ─── Workflow action (Approve / Reject) ────────────────────────────────────
  const handleActionClick = async (pr, type) => {
    const prId = pr.id || pr.purchase_request_id;
    
    // The header GL is no longer required to approve. Each line carries its own
    // cost_control_general_ledger now, and that is the code that actually
    // posts — the header field is only a convenience for filling them all at
    // once. Demanding it blocked the very case it exists to serve: a
    // requisition whose lines legitimately post to different accounts.
    if (type === 'approve' && isCCEditing) {
      setCcHeaderError(null);
    }

    // Every line that will count as payable needs a quantity and a rate.
    // Lines marked rejected (or on hold, at final approval) are exempt — they
    // carry no money forward.
    if (type === 'approve' && (isCCEditing || isApprovalQueue)) {
      const edits = isCCEditing ? (lineEdits[prId] || {}) : (apprEdits[prId] || {});
      const qtyKey = isCCEditing ? 'cc_qty' : 'appr_qty';
      const rateKey = isCCEditing ? 'cc_rate' : 'appr_rate';
      const blank = (v) => v === '' || v === null || v === undefined;

      // Unmarked lines are included on purpose: the action button auto-marks
      // them as approved, so they will carry money and need figures too.
      const rowErrors = {};
      (pr.items || []).forEach((item) => {
        const e = edits[item.item_id] || {};
        // apprEdits keys carry an appr_ prefix (appr_rejected / appr_on_hold); reading e.rejected / e.on_hold
        // found nothing, so a line marked Reject or Hold was still asked for figures.
        const isRejected = isCCEditing ? e.cc_rejected : e.appr_rejected;
        if (isRejected || (!isCCEditing && e.appr_on_hold)) return;
        const err = {};
        if (blank(e[qtyKey])) err.qty = true;
        if (blank(e[rateKey])) err.rate = true;
        if (Object.keys(err).length) rowErrors[item.item_id] = err;
      });

      if (Object.keys(rowErrors).length) {
        setLineFigureErrors((prev) => ({ ...prev, [prId]: rowErrors }));
        toast.show('Enter a quantity and rate on every line you are approving.', 'error');
        return;
      }
      setLineFigureErrors((prev) => ({ ...prev, [prId]: {} }));
    }

    const instanceId = pr.workflow_instance_id;
    const reviewed = isCCEditing ? hasAnyReview(pr, true) : isApprovalQueue ? hasAnyReview(pr, false) : true;
    setActionComment('');
    setNextStepInfo(null);
    setNextApproverId('');
    setReturnTargets([]); setReturnTarget('');
    setConsiderationStatuses([]); setConsiderationStatusId('');

    // Everything the dialog needs is fetched BEFORE it appears. Opening first
    // and filling in afterwards is what produced the flash: the modal rendered
    // at one size, then grew as the Return-to and Consideration pickers
    // arrived. The wait now shows on the button that was pressed.
    setPreparingAction(`${prId}:${type}`);
    try {
      if (type === 'approve') {
        try {
          const details = await api.getNextStepDetails(instanceId);
          const data = details?.data || details;
          setNextStepInfo(data);
          if (data?.approver_mode === 'specific_user' && data?.eligible_users?.length > 0) {
            setNextApproverId(data.eligible_users[0].user_id || data.eligible_users[0].id);
          }
        } catch (err) {
          console.error('Failed to fetch next step details', err);
        }
      }

      if (type === 'return') {
        // Both are optional extras — a failure hides the picker and the server
        // falls back to the step's configured behaviour, rather than blocking
        // the reviewer from returning at all.
        const [targets, statuses] = await Promise.all([
          api.getReturnTargets(instanceId).catch(() => null),
          api.listConsiderationStatuses({ activeOnly: true, returnReasonsOnly: true }).catch(() => null),
        ]);
        setReturnTargets(targets?.targets || []);
        setReturnFromStep(targets?.current_step_name || '');
        setReturnTarget(targets?.default_target || 'requester');
        setConsiderationStatuses(
          Array.isArray(statuses) ? statuses : (statuses?.consideration_statuses || [])
        );
      }
    } finally {
      setPreparingAction(null);
    }

    setActionModal({ prId, instanceId, type, reviewed });
  };

  const submitAction = async () => {
    if (!actionModal) return;
    setSubmittingAction(true);
    try {
      const { instanceId, type, prId } = actionModal;
      // The heading files the return in Pending Consideration, so Cost Control
      // must pick one. Checked only when the picker is on screen.
      if (type === 'return'
          && offersConsiderationStatus(returnFromStep, returnTargets, returnTarget)
          && !considerationStatusId) {
        toast.show('Choose a consideration status for this return.', 'error');
        setSubmittingAction(false);
        return;
      }
      // Resume is exempt alongside approve: it undoes a hold rather than
      // deciding anything, so there is nothing to justify.
      if (!['approve', 'resume'].includes(type) && !actionComment.trim()) {
        const requiredMsg = {
          return: 'Comment is required to return this request.',
          reject: 'Comment is required to reject this request.',
          hold: 'Comment is required to put this request on hold.',
        }[type] || 'Comment is required.';
        toast.show(requiredMsg, 'error');
        setSubmittingAction(false);
        return;
      }
      
      const payload = { action: type, comment: actionComment };
      // Both only meaningful on a return; the server ignores them otherwise.
      if (type === 'return' && returnTarget) payload.return_target = returnTarget;
      if (type === 'return' && considerationStatusId) {
        payload.consideration_status_id = considerationStatusId;
      }
      if (type === 'approve' && nextStepInfo?.approver_mode === 'specific_user') {
        if (!nextApproverId) {
          toast.show('Please select the specific approver.', 'error');
          setSubmittingAction(false);
          return;
        }
        payload.next_approver_id = nextApproverId;
      }
      const prObj = prs.find((p) => (p.id || p.purchase_request_id) === prId);

      // No separate "Save Review" step anymore — whatever's in lineEdits/apprEdits
      // is saved together with the action itself. If nothing was reviewed at
      // all, every line auto-fills to match the action being taken.
      // Skipped for resume — lifting a hold is not a decision on the lines, so
      // there is no review to save alongside it.
      if (prObj && type !== 'resume' && (isCCEditing || isApprovalQueue)) {
        const reviewed = hasAnyReview(prObj, isCCEditing);
        let autoFillStatus = null;
        if (!reviewed) {
          if (type === 'approve') autoFillStatus = 'approved';
          else if (type === 'reject') autoFillStatus = 'rejected';
          else if (type === 'hold') autoFillStatus = 'on_hold';
        }
        if (isCCEditing) {
          await api.updatePRItemsCCReview(prId, { 
            items: buildCCReviewItems(prObj, autoFillStatus),
            cost_control_general_ledger: ccHeaderEdits[prId]?.cost_control_general_ledger !== undefined ? ccHeaderEdits[prId].cost_control_general_ledger : (prObj.cost_control_general_ledger || null)
          });
        } else {
          await api.updatePRItemsApprovalReview(prId, { items: buildApprReviewItems(prObj, autoFillStatus) });
        }
      }

      await api.actOnWorkflow(instanceId, payload);
      setActionModal(null);
      refreshCcCounts();

      // Hold and resume don't move the request off this step, so the row
      // stays — but its status changes, and dropping the row was the only
      // refresh this branch did. Deliberately NOT awaited: awaiting it held
      // the confirmation back until the whole list had reloaded, which is why
      // the modal appeared late. The refresh now runs underneath the
      // confirmation instead of in front of it.
      const refreshInBackground = () => {
        api.clearApiCache();
        fetchPRs().catch(() => { });
      };

      if (type === 'hold' || type === 'resume') {
        refreshInBackground();
      } else {
        setPrs((prev) => prev.filter((p) => (p.id || p.purchase_request_id) !== prId));
        // Pending Consideration renders from the grouped payload, not `prs`,
        // so filtering that alone left the row on screen. Drop it from its
        // group too, and drop the group entirely once it is empty.
        setConsiderationGroups((prev) => {
          if (!prev?.groups) return prev;
          const groups = prev.groups
            .map((g) => ({
              ...g,
              requests: (g.requests || []).filter((r) => (r.id || r.purchase_request_id) !== prId),
            }))
            .map((g) => ({ ...g, count: g.requests.length }))
            .filter((g) => g.count > 0);
          return { ...prev, groups, total: groups.reduce((n, g) => n + g.count, 0) };
        });
      }
      // Show outcome
      if (type === 'approve') {
        const nextStepMessage =
          nextStepInfo && nextStepInfo.approver_mode !== 'is_final' && nextStepInfo.step_name
            ? `has been approved and forwarded to the next step in the workflow — ${nextStepInfo.step_name}.`
            : 'has been approved. This purchase request approval process is now complete.';
        setOutcomeModal({
          type: 'approve',
          title: 'Request Approved!',
          message: `Purchase Request ${prObj?.pr_number || ''} ${nextStepMessage}`,
        });
      } else if (type === 'return') {
        setOutcomeModal({
          type: 'return',
          title: 'Request Returned!',
          message: `Purchase Request ${prObj?.pr_number || ''} has been returned to the requester for correction.`,
        });
      } else if (type === 'reject') {
        setOutcomeModal({
          type: 'reject',
          title: 'Request Rejected!',
          message: `Purchase Request ${prObj?.pr_number || ''} has been rejected. This is final — it cannot be resubmitted.`,
        });
      } else if (type === 'hold') {
        setOutcomeModal({
          type: 'hold',
          title: 'Request Put On Hold!',
          message: `Purchase Request ${prObj?.pr_number || ''} has been put on hold. It stays with you and you can act on it again later.`,
        });
      } else if (type === 'resume') {
        setOutcomeModal({
          type: 'resume',
          title: 'Hold Lifted!',
          message: `Purchase Request ${prObj?.pr_number || ''} is active again and back in your queue for a decision.`,
        });
      }
    } catch (err) {
      toast.show(err.response?.data?.detail || `Failed to ${actionModal.type} request`, 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // ─── Submit to workflow ────────────────────────────────────────────────────
  const handleFetchApprovers = async (prId) => {
    setLoadingApprovers(true);
    try {
      const prObj = prs.find((p) => (p.id || p.purchase_request_id) === prId);
      const res = await api.getEligibleApprovers('PR_APPROVAL', {
        requested_department_id: prObj?.requested_department_id || null,
      });
      const data = res?.data || res;
      setFirstStepInfo(data);
      setSubmittingPrId(prId);
      if (data?.approver_mode === 'specific_user') {
        const users = data.eligible_users || [];
        if (users.length === 0) {
          toast.show('No eligible approvers found for this workflow step.', 'error');
          return;
        }
        setApprovers(users);
        setSelectedApproverId(users[0].user_id || users[0].id);
        setShowApproverModal(true);
      } else {
        setApprovers([]);
        setSelectedApproverId('');
        setShowApproverModal(true);
      }
    } catch (e) {
      toast.show(e.response?.data?.detail || 'Failed to fetch eligible approvers', 'error');
    } finally {
      setLoadingApprovers(false);
    }
  };

  const handleDeletePR = async () => {
    if (!deletingPrId) return;
    try {
      await api.deletePR(deletingPrId);
      toast.show('Requisition deleted successfully!', 'success');
      setShowDeleteModal(false);
      setDeletingPrId(null);
      fetchPRs();
    } catch (e) {
      toast.show(e.response?.data?.detail || 'Failed to delete requisition', 'error');
    }
  };

  const handleSubmit = async (prId, firstApproverId) => {
    // Submitting twice would start a second workflow on the same requisition,
    // so the guard is on the handler, not just the button's disabled state.
    if (submittingWorkflow) return;
    setSubmittingWorkflow(true);
    try {
      await api.submitWorkflow({
        entity_type: 'purchase_request',
        entity_id: prId,
        definition_code: 'PR_APPROVAL',
        first_approver_id: firstStepInfo?.approver_mode === 'specific_user' ? firstApproverId : undefined,
      });
      const prObj = prs.find((p) => (p.id || p.purchase_request_id) === prId);
      setSubmittedPRNumber(prObj?.pr_number || '');
      setShowApproverModal(false);
      setSubmittingPrId(null);
      setShowSubmitSuccessModal(true);
      fetchPRs();
    } catch (e) {
      toast.show(e.response?.data?.detail || 'Failed to submit workflow', 'error');
    } finally {
      setSubmittingWorkflow(false);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading || (isCCQueue && countsInitializing)) return <RNLLoader title="Loading Requisitions..." subtitle="Fetching your data" />;

  if (viewingPrId) {
    return (
      <PRDetailsView
        prId={viewingPrId}
        instanceId={viewingInstanceId}
        viewContext={isCCEditing ? 'cc' : isApprovalQueue ? 'approval' : undefined}
        onEdit={isCCQueue ? undefined : (id) => {
          setViewingPrId(null);
          setEditingPrId(id);
        }}
        onBack={() => {
          api.clearApiCache();
          setViewingPrId(null);
          fetchPRs();
          refreshCcCounts();
        }}
      />
    );
  }

  if (editingPrId) {
    return (
      <NewRequisition
        editPrId={editingPrId}
        onSaveSuccess={() => {
          api.clearApiCache();
          setEditingPrId(null);
          fetchPRs();
        }}
      />
    );
  }

  const uniqueDepartments = Array.from(
    new Set(prs.map((p) => p.department_name || p.department?.name || p.requested_department?.name).filter(Boolean))
  );

  const uniqueProjects = Array.from(
    new Set(prs.map((p) => p.project_name || p.items?.[0]?.project_name).filter(Boolean))
  );

  const uniqueRequesters = Array.from(
    new Set(prs.map((p) =>
      p.requested_by_name ||
      p.requested_by?.full_name ||
      (p.requested_by ? `${p.requested_by.first_name || ''} ${p.requested_by.last_name || ''}`.trim() : '')
    ).filter(Boolean))
  );

  // Plain "My Requisitions" (no queue, no drafts-only scope) hides DRAFT/SUBMITTED
  // by default — those only start showing up once the department head has acted
  // on them (pushed on to the CC queue). The status filter can still be used to
  // explicitly pull them back up if needed.
  const isMyRequisitionsView = !queueStep && !draftsOnly;

  const triggerSearch = () => {
    const term = search.trim();
    setAppliedSearch(term);
    setSearchVersion((v) => v + 1);
    setPage((p) => ({ ...p, skip: 0 }));
  };

  const filteredPrs = prs.filter((p) => {
    const reqName =
      p.requested_by_name ||
      p.requested_by?.full_name ||
      (p.requested_by ? `${p.requested_by.first_name || ''} ${p.requested_by.last_name || ''}`.trim() : '');
    const deptName = p.department_name || p.department?.name || p.requested_department?.name || '';
    const typeName = p.request_type_name || p.purchase_request_type?.name || p.type?.name || '';
    const status = (p.request_status || 'DRAFT').toUpperCase();
    const matchesStatus = draftsOnly
      ? belongsInMyDrafts(p)
      : !statusFilter
      ? (!isMyRequisitionsView
          || (!['DRAFT', 'SUBMITTED'].includes(status) && !isDepartmentBounce(p)))
      : status === statusFilter && !(isMyRequisitionsView && isDepartmentBounce(p));
    const matchesDept = departmentId
      ? String(p.requested_department_id || '') === String(departmentId)
      : !deptFilter || deptName === deptFilter;
    const prProject = p.project_name || p.items?.[0]?.project_name || '';
    const matchesProject = !projectFilter || prProject === projectFilter;
    const matchesRequester = !requesterFilter || reqName === requesterFilter;
    return matchesStatus && matchesDept && matchesProject && matchesRequester;
  });

  const getStatusBg = (st) => {
    const s = (st || '').toUpperCase();
    if (s === 'APPROVED') return '#dcfce7';
    if (s === 'SUBMITTED') return '#dbeafe';
    if (s === 'IN_PROGRESS') return '#ede9fe';
    if (s === 'ON_HOLD') return '#fef3c7';
    if (s === 'RETURNED') return '#ffedd5';
    if (s === 'REJECTED') return '#fee2e2';
    return '#fef9c3';
  };
  const getStatusCol = (st) => {
    const s = (st || '').toUpperCase();
    if (s === 'APPROVED') return '#16a34a';
    if (s === 'SUBMITTED') return '#1d4ed8';
    if (s === 'IN_PROGRESS') return '#7c3aed';
    if (s === 'ON_HOLD') return '#b45309';
    if (s === 'RETURNED') return '#f97316';
    if (s === 'REJECTED') return '#ef4444';
    return '#ca8a04';
  };
  const getStatusLabel = (st) => {
    const s = (st || '').toUpperCase();
    if (s === 'IN_PROGRESS') return 'IN PROGRESS';
    if (s === 'ON_HOLD') return 'ON HOLD';
    return s;
  };
  const getPriorityBadgeStyle = (prio) => {
    const p = (prio || 'normal').toLowerCase();
    if (p === 'urgent') return { color: '#ef4444', bg: '#fee2e2', border: '#fca5a5' };
    if (p === 'high') return { color: '#d97706', bg: '#fef3c7', border: '#fcd34d' };
    return { color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' };
  };

  // ─── Expanded content factory ──────────────────────────────────────────────
  const renderExpandedContent = (pr) => {
    const prId = pr.id || pr.purchase_request_id;
    const prEdits = lineEdits[prId] || {};
    const apprPrEdits = apprEdits[prId] || {};
    // Parked by an approver: the server refuses every decision until the hold
    // is lifted, so the buttons follow the same rule.
    const onHold = (pr.request_status || '').toUpperCase() === 'ON_HOLD';

    const numInput = (val, onChange, hasError = false) => (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <input
          type="number"
          min="0"
          value={val}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: 80,
            padding: '3px 6px',
            border: hasError ? '2px solid #ef4444' : '1px solid #cbd5e1',
            borderRadius: 4,
            fontSize: 12,
            background: '#f8fafc',
            textAlign: 'right',
          }}
        />
        {hasError && (
          <div style={{
            position: 'absolute', top: '-28px', left: 0, background: '#ef4444', color: '#fff',
            padding: '3px 7px', borderRadius: 4, fontSize: 10, fontWeight: 'bold',
            whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            This field is required
            <div style={{
              position: 'absolute', bottom: '-4px', left: 14, width: 0, height: 0,
              borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
              borderTop: '4px solid #ef4444',
            }} />
          </div>
        )}
      </div>
    );

    return (
      <div
        style={{
          padding: '0 0 16px 0',
          background: '#f1f5f9',
          borderTop: '2px solid #3b82f6',
          borderBottom: '2px solid #cbd5e1',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Action bar (PURPOSE + Approve/Reject) ── */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '10px 20px 8px',
            background: '#e8eef8',
            borderBottom: '1px solid #cbd5e1',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            PURPOSE: {pr.title}
          </div>

          {(() => {
            const totalRequested = Number(pr.total_estimated_cost || 0);
            const ccApproved = (pr.items || []).reduce((sum, it) => sum + Number(it.cost_control_amount ?? it.estimated_amount ?? 0), 0);
            const fieldLabel = { fontWeight: 700, color: '#64748b', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 };
            const fieldValue = { fontWeight: 700, color: '#0f172a', fontSize: 12.5, marginTop: 2 };

            return (
              <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '12px 16px', marginBottom: isApprovalQueue ? 12 : 0, marginTop: 4 }}>
                <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
                  Section 1 — Requisition Header
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', rowGap: 14, columnGap: 24 }}>
                  <div>
                    <div style={fieldLabel}>Ref ID</div>
                    <div style={fieldValue}>{pr.pr_number || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Date</div>
                    <div style={fieldValue}>{(pr.required_date || pr.created_at) ? fmtDate(pr.required_date || pr.created_at) : '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Requester</div>
                    <div style={fieldValue}>{pr.requested_by_name || '—'}</div>
                  </div>

                  <div>
                    <div style={fieldLabel}>Employee ID</div>
                    <div style={fieldValue}>{pr.employee_id || '—'}</div>
                  </div>
                  {/* Who the requisition is FOR, when raised on someone's
                      behalf. Shown even when empty so a reviewer can tell
                      "nobody else" from "nobody filled it in". */}
                  <div>
                    <div style={fieldLabel}>Requested For</div>
                    <div style={fieldValue}>{pr.requested_for || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Designation</div>
                    <div style={fieldValue}>{pr.designation || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Department</div>
                    <div style={fieldValue}>{pr.department_name || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Request Type</div>
                    <div style={fieldValue}>{pr.request_type_name || '—'}</div>
                  </div>

                  <div>
                    <div style={fieldLabel}>Project / Site</div>
                    <div style={fieldValue}>{pr.project_name || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Priority</div>
                    <div style={fieldValue}>{pr.priority_status || 'Normal'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Due Date</div>
                    <div style={fieldValue}>{pr.expected_due_date ? fmtDate(pr.expected_due_date) : '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Purpose</div>
                    <div style={fieldValue}>{pr.title || '—'}</div>
                  </div>

                  <div>
                    <div style={fieldLabel}>Remarks</div>
                    <div style={fieldValue}>{pr.description || pr.remarks || '—'}</div>
                  </div>
                </div>
              </div>
            );
          })()}


          {isApprovalQueue && (() => {
            const rejectedLines = pr.cc_rejected_line_count != null
              ? pr.cc_rejected_line_count
              : (pr.items || []).filter((it) => it.cost_control_rejected).length;
            const approvedLines = (pr.items || []).filter((it) => it.cost_control_approved).length;
            const totalRequested = Number(pr.total_estimated_cost || 0);
            const ccApproved = (pr.items || []).reduce((sum, it) => sum + Number(it.cost_control_amount ?? it.estimated_amount ?? 0), 0);
            const variance = totalRequested - ccApproved;
            const withinBudget = variance >= 0;

            const fieldLabel = { fontWeight: 700, color: '#64748b', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 };
            const fieldValue = { fontWeight: 700, color: '#0f172a', fontSize: 12.5, marginTop: 2 };

            return (
              <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontWeight: 800, color: '#2563eb', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>
                  Section 2 — Cost Control Review Summary
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', rowGap: 14, columnGap: 24 }}>
                  <div>
                    <div style={fieldLabel}>CC Officer</div>
                    <div style={fieldValue}>{pr.cc_reviewed_by || '—'}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>CC Review Date</div>
                    <div style={fieldValue}>{pr.cc_reviewed_at ? fmtDate(pr.cc_reviewed_at) : '—'}</div>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <div style={fieldLabel}>CC Comment</div>
                    <div style={{ ...fieldValue, fontWeight: 600, fontStyle: pr.cc_review_comment ? 'italic' : 'normal', lineHeight: 1.5 }}>
                      {pr.cc_review_comment ? `"${pr.cc_review_comment}"` : '—'}
                    </div>
                  </div>

                  <div>
                    <div style={fieldLabel}>Approved Lines</div>
                    <div style={fieldValue}>{approvedLines}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Rejected Lines</div>
                    <div style={{ ...fieldValue, color: rejectedLines > 0 ? '#ef4444' : '#0f172a' }}>{rejectedLines}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Total Requested</div>
                    <div style={fieldValue}>₦{fmt(totalRequested)}</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>CC Approved</div>
                    <div style={{ ...fieldValue, color: '#2563eb' }}>₦{fmt(ccApproved)}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: withinBudget ? '#16a34a' : '#ef4444' }} />
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: withinBudget ? '#16a34a' : '#ef4444' }}>
                      {withinBudget ? 'Within Budget' : 'Over Budget'}
                    </span>
                  </div>
                  <div>
                    <span style={{ ...fieldLabel, marginRight: 6 }}>Variance</span>
                    <span style={{ fontWeight: 800, fontSize: 12.5, color: withinBudget ? '#16a34a' : '#ef4444' }}>₦{fmt(Math.abs(variance))}</span>
                  </div>
                </div>
              </div>
            );
          })()}

        </div>

        {/* Everything the requester filled in, read-only, above the working
            grid. It lives here rather than as more columns on that grid
            because the grid is where Cost Control and the approver TYPE — and
            every column added there pushes the inputs further off-screen.
            Shown only in the queues, where somebody is reviewing what was
            asked for. */}
        {(isCCQueue || isApprovalQueue) && <RequestedLineItems items={pr.items || []} />}

        {/* A held request is parked — approve / return / reject are all
            refused by the server until the hold is lifted, so they are
            disabled here rather than offered and then rejected. Resume stays
            live: it is the only way out. */}
        {/* Same 16px inset as the heading above: the controls sit inside the
            panel walls while the two tables stay edge to edge. */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '0 16px' }}>
          {(isCCEditing || isApprovalQueue) && (() => {
          // A request a later step sent BACK is not being approved onward —
          // correcting it and sending it on returns it to the step that
          // asked for the change. The button says so, matching the dialog it
          // opens ("Goes back to – Approval Queue").
          const sendingBack = isAwaitingCorrection(pr);
          const backTo = sendingBack ? correctionTarget(pr) : '';
          return (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {isCCEditing ? 'Cost Control Actions:' : 'Approval Actions:'}
              </div>
              {/* Approve */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(pr, 'approve');
                }}
                disabled={!!preparingAction || onHold}
                style={{
                  background: '#16a34a',
                  color: '#fff',
                  border: '1.5px solid #16a34a',
                  padding: '5px 16px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: preparingAction ? 'wait' : (onHold ? 'not-allowed' : 'pointer'),
                  opacity: (preparingAction || onHold) ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  transition: 'all 0.2s',
                  filter: onHold ? 'grayscale(0.35)' : 'none',
                }}
                title={onHold
                  ? 'This request is on hold — resume it first.'
                  : (sendingBack ? `Send the corrected request back to ${backTo}` : 'Approve this request')}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {sendingBack ? `Resubmit to ${backTo}` : 'Approve'}
              </button>

              {/* Return and Reject stay available even while this desk is
                  holding a CORRECTION. Cost Control asked for them back:
                  when the Approval Queue sends something down, Cost Control
                  may well conclude the fix belongs further back still — with
                  the department head, or with the requester — rather than
                  correcting it themselves and pushing it straight up again.
                  Returning re-files it under whichever consideration status
                  they choose, so it leaves "Returned By Approval Queue". */}
              <>
              {/* Return */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(pr, 'return');
                }}
                disabled={!!preparingAction || onHold}
                style={{
                  background: '#f97316',
                  color: '#fff',
                  border: '1.5px solid #f97316',
                  padding: '5px 16px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: preparingAction ? 'wait' : (onHold ? 'not-allowed' : 'pointer'),
                  opacity: (preparingAction || onHold) ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  transition: 'all 0.2s',
                  filter: onHold ? 'grayscale(0.35)' : 'none',
                }}
                title={onHold ? 'This request is on hold — resume it first.' : "Return this request to the requester"}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" />
                </svg>
                Return
              </button>

              {/* Hold and Resume are the same slot: a request is either
                  parked or it is not, so offering both at once would leave
                  one of them a no-op. Before this, the only way out of a
                  hold was to approve, return or reject it — an approver who
                  parked a request to ask a question could not simply
                  un-park it once they had the answer. */}
              {isApprovalQueue && (pr.request_status || '').toUpperCase() === 'ON_HOLD' ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionClick(pr, 'resume');
                  }}
                  disabled={!!preparingAction}
                  style={{
                    background: '#16a34a',
                    color: '#fff',
                    border: '1.5px solid #16a34a',
                    padding: '5px 16px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: preparingAction ? 'wait' : 'pointer',
                    opacity: preparingAction ? 0.55 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    transition: 'all 0.2s',
                  }}
                  title="Lift the hold and put this request back in the queue"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  Resume
                </button>
              ) : isApprovalQueue ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionClick(pr, 'hold');
                  }}
                  disabled={!!preparingAction}
                  style={{
                    background: '#eab308',
                    color: '#fff',
                    border: '1.5px solid #eab308',
                    padding: '5px 16px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    transition: 'all 0.2s',
                  }}
                  title="Put this request on hold"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                  </svg>
                  Hold
                </button>
              ) : null}

              {/* Reject — available to both Cost Control and Approval; a CC
                  reject is terminal, same as Approval's. */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(pr, 'reject');
                }}
                disabled={!!preparingAction || onHold}
                style={{
                  background: '#ef4444',
                  color: '#fff',
                  border: '1.5px solid #ef4444',
                  padding: '5px 16px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: preparingAction ? 'wait' : (onHold ? 'not-allowed' : 'pointer'),
                  opacity: (preparingAction || onHold) ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  transition: 'all 0.2s',
                  filter: onHold ? 'grayscale(0.35)' : 'none',
                }}
                title={onHold ? 'This request is on hold — resume it first.' : "Reject this request permanently"}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                Reject
              </button>
              </>
            </>
          );
          })()}

          <div style={{ marginLeft: (isCCEditing || isApprovalQueue) ? 16 : 0, display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#475569' }}>Cost Control GL:</span>
            <input
              type="text"
              value={isCCEditing ? (ccHeaderEdits[pr.id || pr.purchase_request_id]?.cost_control_general_ledger ?? pr.cost_control_general_ledger ?? '') : (pr.cost_control_general_ledger || '')}
              onChange={(e) => {
                if (!isCCEditing) return;
                setCcHeaderEdits(prev => ({
                  ...prev,
                  [pr.id || pr.purchase_request_id]: { ...prev[pr.id || pr.purchase_request_id], cost_control_general_ledger: e.target.value }
                }));
                if (glAppliesToAll(pr.id || pr.purchase_request_id)) {
                  applyHeaderGlToLines(pr, e.target.value);
                }
                if (e.target.value.trim() && ccHeaderError === (pr.id || pr.purchase_request_id)) {
                  setCcHeaderError(null);
                }
              }}
              disabled={!isCCEditing}
              placeholder={isCCEditing ? "Enter GL code" : "—"}
              style={{
                padding: '5px 10px',
                fontSize: 12,
                border: ccHeaderError === (pr.id || pr.purchase_request_id) ? '2px solid #ef4444' : '1px solid #cbd5e1',
                borderRadius: 6,
                outline: 'none',
                background: isCCEditing ? '#fff' : '#f8fafc',
                cursor: isCCEditing ? 'text' : 'not-allowed',
                width: 180,
                fontWeight: 600,
                color: '#0f172a'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            {isCCEditing && (
              <label
                onClick={(e) => e.stopPropagation()}
                title="On: the header code above fills the CC GL column for every line. Off: type a code on each line."
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  fontSize: 11.5, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap',
                }}
              >
                <input
                  type="checkbox"
                  checked={glAppliesToAll(pr.id || pr.purchase_request_id)}
                  onChange={(ev) => {
                    const prId = pr.id || pr.purchase_request_id;
                    const on = ev.target.checked;
                    setGlAllLines((prev) => ({ ...prev, [prId]: on }));
                    // Turning it ON re-applies the header code immediately.
                    // Turning it OFF leaves the lines exactly as they are —
                    // they become editable, and blanking them would throw
                    // away codes the reviewer is about to adjust.
                    if (on) {
                      applyHeaderGlToLines(
                        pr,
                        ccHeaderEdits[prId]?.cost_control_general_ledger
                          ?? pr.cost_control_general_ledger ?? '',
                      );
                    }
                  }}
                  style={{ cursor: 'pointer', width: 14, height: 14 }}
                />
                Same GL for every line
              </label>
            )}
            {ccHeaderError === (pr.id || pr.purchase_request_id) && (
              <div style={{
                position: 'absolute',
                top: '-28px',
                right: 0,
                background: '#ef4444',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                whiteSpace: 'nowrap',
                zIndex: 10,
                pointerEvents: 'none'
              }}>
                This field is required
                <div style={{
                  position: 'absolute',
                  bottom: '-4px',
                  right: '16px',
                  width: 0,
                  height: 0,
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderTop: '4px solid #ef4444'
                }}></div>
              </div>
            )}
          </div>
          {/* Copy-values sits on the same row as the action buttons rather
              than in a strip of its own: it is part of the same bar of
              controls, and giving it its own row pushed "LINE ITEMS AS
              REQUESTED" out of line with the panels above it.
              marginLeft:auto holds it hard right. */}
      {isCCEditing && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          marginLeft: 'auto',
        }}>
          <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>
            Cost Control values:
          </span>
          <button
            type="button"
            disabled={['returned', 'resubmitted'].includes(pr.queue_arrival)}
            onClick={(ev) => { ev.stopPropagation(); copyRequestToCC(pr); }}
            title={!['returned', 'resubmitted'].includes(pr.queue_arrival) ? "Copy the requester's quantity, rate and amount into the Cost Control columns for every line. You can still change any line afterwards." : "This request came back to Cost Control after a return: keep the earlier review figures and adjust them line by line."}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', background: '#fff', color: '#1e3a8a',
              border: '1.5px solid #1e3a8a', borderRadius: 8,
              fontSize: 12, fontWeight: 800, cursor: !['returned', 'resubmitted'].includes(pr.queue_arrival) ? 'pointer' : 'not-allowed', opacity: !['returned', 'resubmitted'].includes(pr.queue_arrival) ? 1 : 0.45,
              fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all 0.15s',
            }}
            onMouseOver={(ev) => { if (['returned', 'resubmitted'].includes(pr.queue_arrival)) return; ev.currentTarget.style.background = '#1e3a8a'; ev.currentTarget.style.color = '#fff'; }}
            onMouseOut={(ev) => { ev.currentTarget.style.background = '#fff'; ev.currentTarget.style.color = '#1e3a8a'; }}
          >
            ⧉ Copy requester values
          </button>
        </div>
      )}
      {isApprovalQueue && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          marginLeft: 'auto',
        }}>
          <span style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>
            Approved values:
          </span>
          <button
            type="button"
            disabled={pr.queue_arrival === 'resubmitted'}
            onClick={(ev) => { ev.stopPropagation(); copyCCToApproval(pr); }}
            title={pr.queue_arrival !== 'resubmitted' ? "Copy Cost Control's quantity, rate and amount into the approved columns for every line. You can still change any line afterwards." : "The requester corrected this request after it was returned, so the Cost Control figures are from before the correction."}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', background: '#fff', color: '#1e3a8a',
              border: '1.5px solid #1e3a8a', borderRadius: 8,
              fontSize: 12, fontWeight: 800, cursor: pr.queue_arrival !== 'resubmitted' ? 'pointer' : 'not-allowed', opacity: pr.queue_arrival !== 'resubmitted' ? 1 : 0.45,
              fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all 0.15s',
            }}
            onMouseOver={(ev) => { if (pr.queue_arrival === 'resubmitted') return; ev.currentTarget.style.background = '#1e3a8a'; ev.currentTarget.style.color = '#fff'; }}
            onMouseOut={(ev) => { ev.currentTarget.style.background = '#fff'; ev.currentTarget.style.color = '#1e3a8a'; }}
          >
            ⧉ Copy CC values
          </button>
          <button
            type="button"
            disabled={pr.queue_arrival !== 'resubmitted'}
            onClick={(ev) => { ev.stopPropagation(); copyRequestToApproval(pr); }}
            title={pr.queue_arrival === 'resubmitted' ? "Copy the requested quantity, estimated rate and amount into the approved columns for every line. You can still change any line afterwards." : "Available when the requester has corrected and resubmitted a request the Approval Queue returned."}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', background: '#fff', color: '#1e3a8a',
              border: '1.5px solid #1e3a8a', borderRadius: 8,
              fontSize: 12, fontWeight: 800, cursor: pr.queue_arrival === 'resubmitted' ? 'pointer' : 'not-allowed', opacity: pr.queue_arrival === 'resubmitted' ? 1 : 0.45,
              fontFamily: 'var(--font)', whiteSpace: 'nowrap', transition: 'all 0.15s',
            }}
            onMouseOver={(ev) => { if (pr.queue_arrival !== 'resubmitted') return; ev.currentTarget.style.background = '#1e3a8a'; ev.currentTarget.style.color = '#fff'; }}
            onMouseOut={(ev) => { ev.currentTarget.style.background = '#fff'; ev.currentTarget.style.color = '#1e3a8a'; }}
          >
            ⧉ Copy requester values
          </button>
        </div>
      )}
        </div>

        {/* ── Line Items Table ── */}
        {/* No side padding: the grid runs edge to edge of the panel, so the
            columns get every pixel available before anything has to scroll. */}
        <div style={{ overflowX: 'auto', padding: '12px 0 0' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12,
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <thead>
              <tr style={{ background: '#0b318b', color: '#fff' }}>
                {isCCQueue && (
                  <>
                    <th style={thS({ background: '#1e3a8a', width: 84 })}>ACTION</th>
                    <th style={thS({ background: '#15317a', width: 130 })}>{isCCEditing ? 'DECISION' : 'OUTCOME'}</th>
                  </>
                )}
                {isApprovalQueue && (
                  <>
                    <th style={thS({ background: '#1e3a8a', width: 84 })}>ACTION</th>
                    <th style={thS({ background: '#15317a', width: 130 })}>OUTCOME</th>
                  </>
                )}
                <th style={thS({ minWidth: 70 })}>#</th>
                {/* Description and Unit are not repeated here. They are in
                    "LINE ITEMS AS REQUESTED" above, in full, and this grid is
                    where figures get typed — the narrower it is, the less
                    scrolling stands between a reviewer and the inputs. The #
                    column is what ties a row here to the one above it. */}
                {isCCEditing && (
                  <>
                    <th style={thS({ background: '#0e4429', minWidth: 130 })}>PROJECT</th>
                    <th style={thS({ background: '#0e4429', minWidth: 150 })}>BOQ ITEM</th>
                    <th style={thS({ background: '#0e4429', minWidth: 150 })}>BOQ SUB-COMPONENT</th>
                  </>
                )}
                <th style={thS()}>REQ QTY</th>
                <th style={thS()}>EST RATE (₦)</th>
                <th style={thS()}>EST AMT (₦)</th>
                <th style={thS()}>VAT (₦)</th>
                {isCCQueue && (
                  <>
                    <th style={thS({ background: '#1e3a8a', minWidth: 90 })}>CC QTY</th>
                    <th style={thS({ background: '#1e3a8a', minWidth: 90 })}>CC RATE (₦)</th>
                    <th style={thS({ background: '#1e3a8a', minWidth: 90 })}>CC AMT (₦)</th>
                    <th style={thS({ background: '#1e3a8a', minWidth: 110 })}>CC GL</th>
                  </>
                )}
                {isApprovalQueue && (
                  <>
                    <th style={thS({ background: '#0e7490', minWidth: 80 })}>CC QTY</th>
                    <th style={thS({ background: '#0e7490', minWidth: 90 })}>CC RATE (₦)</th>
                    <th style={thS({ background: '#0e7490', minWidth: 90 })}>CC AMT (₦)</th>
                    {/* The ledger each line actually posts to. Read-only here:
                        the approver needs to see what Cost Control set, not
                        change it. */}
                    <th style={thS({ background: '#0e7490', minWidth: 110 })}>CC GL</th>
                    <th style={thS({ background: '#0e7490', minWidth: 120 })}>CC DECISION</th>
                    <th style={thS({ background: '#1e3a8a', minWidth: 80 })}>APPR QTY</th>
                    <th style={thS({ background: '#1e3a8a', minWidth: 90 })}>APPR RATE (₦)</th>
                    <th style={thS({ background: '#1e3a8a', minWidth: 90 })}>APPR AMT (₦)</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {(pr.items || []).map((item, idx) => {
                const e = prEdits[item.item_id] || {};
                const ae = apprPrEdits[item.item_id] || {};
                return (
                  <tr key={item.item_id} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    {isCCQueue && (
                      <>
                        <td style={tdS({ background: '#f0f6ff', textAlign: 'center' })}>
                          {isCCEditing ? (
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                              <button
                                onClick={(e2) => { e2.stopPropagation(); setCCStatus(prId, item.item_id, e.cc_approved ? 'none' : 'approved'); }}
                                title="Accept"
                                style={{
                                  width: 26,
                                  height: 26,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1.5px solid #16a34a',
                                  borderRadius: 6,
                                  fontSize: 13,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                  cursor: 'pointer',
                                  background: e.cc_approved ? '#16a34a' : 'transparent',
                                  color: e.cc_approved ? '#fff' : '#16a34a',
                                  transition: 'all 0.15s',
                                }}
                              >
                                ✓
                              </button>
                              <button
                                onClick={(e2) => { e2.stopPropagation(); setCCStatus(prId, item.item_id, e.cc_rejected ? 'none' : 'rejected'); }}
                                title="Reject"
                                style={{
                                  width: 26,
                                  height: 26,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1.5px solid #ef4444',
                                  borderRadius: 6,
                                  fontSize: 13,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                  cursor: 'pointer',
                                  background: e.cc_rejected ? '#ef4444' : 'transparent',
                                  color: e.cc_rejected ? '#fff' : '#ef4444',
                                  transition: 'all 0.15s',
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div
                              title={e.cc_approved ? 'Accepted' : e.cc_rejected ? 'Rejected' : 'No decision recorded'}
                              style={{
                                width: 26,
                                height: 26,
                                margin: '0 auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 6,
                                fontSize: 13,
                                fontWeight: 700,
                                lineHeight: 1,
                                border: '1.5px solid ' + (e.cc_approved ? '#16a34a' : e.cc_rejected ? '#ef4444' : '#cbd5e1'),
                                background: e.cc_approved ? '#16a34a' : e.cc_rejected ? '#ef4444' : 'transparent',
                                color: e.cc_approved || e.cc_rejected ? '#fff' : '#94a3b8',
                              }}
                            >
                              {e.cc_approved ? '✓' : e.cc_rejected ? '✕' : '—'}
                            </div>
                          )}
                          {e.cc_rejected && e.cc_rejected_reason && (
                            <div style={{ marginTop: 3, fontSize: 10, color: '#ef4444', fontStyle: 'italic', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              "{e.cc_rejected_reason}"
                            </div>
                          )}
                        </td>
                        <td style={tdS({ background: '#e8f1ff', textAlign: 'center', fontWeight: 700 })}>
                          {e.cc_approved ? (
                            <span style={{ color: '#16a34a' }}>CC Approved</span>
                          ) : e.cc_rejected ? (
                            <span style={{ color: '#ef4444' }}>CC Rejected</span>
                          ) : (
                            <span style={{ color: '#94a3b8', fontWeight: 400 }}>—</span>
                          )}
                        </td>
                      </>
                    )}
                    {isApprovalQueue && (
                      <>
                        <td style={tdS({ background: '#f0f6ff', textAlign: 'center' })}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                            <button
                              onClick={(e2) => { e2.stopPropagation(); setApprStatus(prId, item.item_id, ae.appr_approved ? 'none' : 'approved'); }}
                              title="Accept"
                              style={{
                                width: 26,
                                height: 26,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1.5px solid #16a34a',
                                borderRadius: 6,
                                fontSize: 13,
                                fontWeight: 700,
                                lineHeight: 1,
                                cursor: 'pointer',
                                background: ae.appr_approved ? '#16a34a' : 'transparent',
                                color: ae.appr_approved ? '#fff' : '#16a34a',
                                transition: 'all 0.15s',
                              }}
                            >
                              ✓
                            </button>
                            <button
                              onClick={(e2) => { e2.stopPropagation(); setApprStatus(prId, item.item_id, ae.appr_on_hold ? 'none' : 'on_hold'); }}
                              title="Hold"
                              style={{
                                width: 26,
                                height: 26,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1.5px solid #eab308',
                                borderRadius: 6,
                                fontSize: 13,
                                fontWeight: 700,
                                lineHeight: 1,
                                cursor: 'pointer',
                                background: ae.appr_on_hold ? '#eab308' : 'transparent',
                                color: ae.appr_on_hold ? '#fff' : '#a16207',
                                transition: 'all 0.15s',
                              }}
                            >
                              ⏸
                            </button>
                            <button
                              onClick={(e2) => { e2.stopPropagation(); setApprStatus(prId, item.item_id, ae.appr_rejected ? 'none' : 'rejected'); }}
                              title="Reject"
                              style={{
                                width: 26,
                                height: 26,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1.5px solid #ef4444',
                                borderRadius: 6,
                                fontSize: 13,
                                fontWeight: 700,
                                lineHeight: 1,
                                cursor: 'pointer',
                                background: ae.appr_rejected ? '#ef4444' : 'transparent',
                                color: ae.appr_rejected ? '#fff' : '#ef4444',
                                transition: 'all 0.15s',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                          {(ae.appr_rejected || ae.appr_on_hold) && ae.appr_reason && (
                            <div style={{ marginTop: 3, fontSize: 10, color: ae.appr_rejected ? '#ef4444' : '#a16207', fontStyle: 'italic', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              "{ae.appr_reason}"
                            </div>
                          )}
                        </td>
                        <td style={tdS({ background: '#e8f1ff', textAlign: 'center', fontWeight: 700 })}>
                          {ae.appr_approved ? (
                            <span style={{ color: '#16a34a' }}>Approved</span>
                          ) : ae.appr_rejected ? (
                            <span style={{ color: '#ef4444' }}>Rejected</span>
                          ) : ae.appr_on_hold ? (
                            <span style={{ color: '#a16207' }}>On Hold</span>
                          ) : (
                            <span style={{ color: '#94a3b8', fontWeight: 400 }}>—</span>
                          )}
                        </td>
                      </>
                    )}
                    <td style={tdS({ textAlign: 'center', color: '#64748b' })}>{idx + 1}</td>
                    {/* see the header note: both live in the read-only table above */}
                    {isCCEditing && (() => {
                      const rowBoqs = boqDataByProject[e.project_id] || [];
                      const selectedBoq = rowBoqs.find((b) => String(b.boq_item_id || b.id) === String(e.boq_item_id));
                      const subBoqOptions = selectedBoq?.components || [];
                      return (
                        <>
                          <td style={tdS({ background: '#f0fdf4' })} onClick={(ev) => ev.stopPropagation()}>
                            <SearchableSelect
                              value={e.project_id || ''}
                              onChange={(v) => updateItemProjectOrBoq(prId, item.item_id, 'project_id', v)}
                              style={ccSelectStyle}
                              options={projects.map((p) => ({ value: p.id || p.project_id, label: p.project_name || p.name }))}
                            />
                          </td>
                          <td style={tdS({ background: '#f0fdf4' })} onClick={(ev) => ev.stopPropagation()}>
                            <SearchableSelect
                              value={e.boq_item_id || ''}
                              onChange={(v) => updateItemProjectOrBoq(prId, item.item_id, 'boq_item_id', v)}
                              disabled={!e.project_id}
                              style={!e.project_id ? ccSelectDisabledStyle : ccSelectStyle}
                              options={rowBoqs.map((b) => ({ value: b.boq_item_id || b.id, label: `${b.boq_code || ''} — ${b.description}` }))}
                              placeholder="Select BOQ"
                            />
                          </td>
                          <td style={tdS({ background: '#f0fdf4' })} onClick={(ev) => ev.stopPropagation()}>
                            <SearchableSelect
                              value={e.boq_component_id || ''}
                              onChange={(v) => updateItemProjectOrBoq(prId, item.item_id, 'boq_component_id', v)}
                              disabled={!e.boq_item_id || subBoqOptions.length === 0}
                              style={(!e.boq_item_id || subBoqOptions.length === 0) ? ccSelectDisabledStyle : ccSelectStyle}
                              options={subBoqOptions.map((c) => ({ value: c.component_id || c.id, label: c.description || c.name }))}
                              placeholder="Select Sub-BOQ"
                            />
                          </td>
                        </>
                      );
                    })()}
                    <td style={tdS({ fontWeight: 600 })}>{fmtNum(item.quantity)}</td>
                    <td style={tdS()}>₦{fmt(item.estimated_unit_rate)}</td>
                    <td style={tdS({ fontWeight: 700 })}>₦{fmt(item.estimated_amount)}</td>
                    <td style={tdS()}>{vatCell(item)}</td>
                    {isCCQueue && (
                      <>
                        <td style={tdS({ background: '#f0f6ff', textAlign: isCCEditing ? 'center' : 'right' })}>
                          {isCCEditing
                            ? numInput(e.cc_qty, (v) => updateLineEdit(prId, item.item_id, 'cc_qty', v), !!lineFigureErrors[prId]?.[item.item_id]?.qty)
                            : (e.cc_qty !== '' && e.cc_qty != null ? fmtNum(e.cc_qty) : '—')}
                        </td>
                        <td style={tdS({ background: '#f0f6ff', textAlign: isCCEditing ? 'center' : 'right' })}>
                          {isCCEditing
                            ? numInput(e.cc_rate, (v) => updateLineEdit(prId, item.item_id, 'cc_rate', v), !!lineFigureErrors[prId]?.[item.item_id]?.rate)
                            : (e.cc_rate !== '' && e.cc_rate != null ? `₦${fmt(e.cc_rate)}` : '—')}
                        </td>
                        <td style={tdS({ background: '#f0f6ff', fontWeight: 600, color: '#1e40af' })}>
                          {e.cc_amount !== '' && e.cc_amount != null ? `₦${fmt(e.cc_amount)}` : '—'}
                        </td>
                        {/* Disabled, not hidden, while the header switch is on:
                            the reviewer can still see what each line will post
                            to, and the box is where they expect it the moment
                            they turn the switch off. */}
                        <td style={tdS({ background: '#f0f6ff', textAlign: 'center' })}>
                          {isCCEditing ? (
                            <input
                              type="text"
                              value={e.cc_gl ?? ''}
                              onChange={(ev) => updateLineEdit(prId, item.item_id, 'cc_gl', ev.target.value)}
                              disabled={glAppliesToAll(prId)}
                              placeholder={glAppliesToAll(prId) ? 'From header' : 'GL code'}
                              title={glAppliesToAll(prId)
                                ? 'Filled from the header GL. Untick "Same GL for every line" to set this line on its own.'
                                : 'Ledger code for this line'}
                              style={{
                                width: 96, padding: '4px 6px', fontSize: 11.5, textAlign: 'center',
                                border: '1px solid #cbd5e1', borderRadius: 5, outline: 'none',
                                background: glAppliesToAll(prId) ? '#f1f5f9' : '#fff',
                                color: glAppliesToAll(prId) ? '#64748b' : 'inherit',
                                cursor: glAppliesToAll(prId) ? 'not-allowed' : 'text',
                              }}
                            />
                          ) : (e.cc_gl || '—')}
                        </td>
                      </>
                    )}
                    {isApprovalQueue && (
                      <>
                        {/* CC reviewed values — read only */}
                        <td style={tdS({ background: '#f0fdfa' })}>{fmtNum(item.cost_control_quantity)}</td>
                        <td style={tdS({ background: '#f0fdfa', textAlign: 'right' })}>
                          {item.cost_control_unit_rate != null ? `₦${fmt(item.cost_control_unit_rate)}` : '—'}
                        </td>
                        <td style={tdS({ background: '#f0fdfa', textAlign: 'right', fontWeight: 600, color: '#0f766e' })}>
                          {item.cost_control_amount != null ? `₦${fmt(item.cost_control_amount)}` : '—'}
                        </td>
                        <td style={tdS({ background: '#f0fdfa' })}>
                          {/* Falls back to the requisition's header code for
                              lines reviewed before per-line GLs existed. */}
                          {item.cost_control_general_ledger || pr.cost_control_general_ledger || '—'}
                        </td>
                        <td style={tdS({ background: '#f0fdfa' })}>
                          {item.cost_control_approved && <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ Accepted</span>}
                          {item.cost_control_rejected && <span style={{ color: '#ef4444', fontWeight: 700 }}>✕ Rejected</span>}
                          {!item.cost_control_approved && !item.cost_control_rejected && <span style={{ color: '#94a3b8' }}>—</span>}
                          {item.cost_control_rejected && item.cost_control_rejected_reason && (
                            <div style={{ marginTop: 3, fontSize: 10, color: '#ef4444', fontStyle: 'italic', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              "{item.cost_control_rejected_reason}"
                            </div>
                          )}
                        </td>

                        {/* Approval values — editable */}
                        <td style={tdS({ background: '#f0f6ff', textAlign: 'center' })}>
                          {numInput(ae.appr_qty, (v) => updateApprEdit(prId, item.item_id, 'appr_qty', v), !!lineFigureErrors[prId]?.[item.item_id]?.qty)}
                        </td>
                        <td style={tdS({ background: '#f0f6ff', textAlign: 'center' })}>
                          {numInput(ae.appr_rate, (v) => updateApprEdit(prId, item.item_id, 'appr_rate', v), !!lineFigureErrors[prId]?.[item.item_id]?.rate)}
                        </td>
                        <td style={tdS({ background: '#f0f6ff', fontWeight: 600, color: '#1e40af' })}>
                          {ae.appr_amount !== '' && ae.appr_amount != null ? `₦${fmt(ae.appr_amount)}` : '—'}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* status hint */}
        {isCCReviewedView && (
          <div style={{ padding: '6px 16px 0', fontSize: 11, color: '#16a34a', fontWeight: 700, fontStyle: 'italic' }}>
            ✓ Already reviewed by Cost Control — view only.
          </div>
        )}
        {isCCRejectedView && (
          <div style={{ padding: '6px 16px 0', fontSize: 11, color: '#dc2626', fontWeight: 700, fontStyle: 'italic' }}>
            ⛔ Rejected by Cost Control — view only.
          </div>
        )}
      </div>
    );
  };

  // ─── Table style helpers ───────────────────────────────────────────────────
  const thS = (extra = {}) => ({
    padding: '9px 10px',
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 11,
    whiteSpace: 'nowrap',
    ...extra,
  });
  const tdS = (extra = {}) => ({
    padding: '8px 10px',
    textAlign: 'center',
    fontSize: 12,
    ...extra,
  });

  // Build table rows
  // The projection is built first and sorted as a whole, rather than sorting
  // the raw requisitions with a second set of accessors. One definition of what
  // a column holds means the order can never disagree with what is displayed —
  // "Project" reads "Multiple Projects" here, and that is what it sorts by too.
  const projectedRows = filteredPrs.map((pr) => {
    const prId = pr.id || pr.purchase_request_id;
    const prProjectName = pr.project_name || pr.items?.[0]?.project_name || '—';
    const isMultipleProjects = pr.items?.length > 1 && new Set(pr.items.map((i) => i.project_id)).size > 1;

    return ({
      _isGroupHeader: false,
      id: prId,
      pr_number: pr.pr_number || 'DRAFT',
      date: pr.required_date || pr.created_at?.split('T')[0] || '—',
      requester:
        pr.requested_by_name ||
        pr.requested_by?.full_name ||
        (pr.requested_by ? `${pr.requested_by.first_name || ''} ${pr.requested_by.last_name || ''}`.trim() : '') || '—',
      department: pr.department_name || pr.department?.name || pr.requested_department?.name || '—',
      type: pr.request_type_name || pr.purchase_request_type?.name || pr.type?.name || '—',
      project: isMultipleProjects ? 'Multiple Projects' : prProjectName,
      priority: pr.priority_status || pr.items?.[0]?.priority_status || 'Normal',
      lines: pr.items?.length || 0,
      total_estimated_cost: pr.total_estimated_cost || 0,
      approved_amount: (pr.request_status || '').toUpperCase() === 'APPROVED' ? pr.total_estimated_cost : 0,
      paid_amount: 0,
      balance_amount: (pr.request_status || '').toUpperCase() === 'APPROVED' ? pr.total_estimated_cost : 0,
      request_status: (pr.request_status || 'DRAFT').toUpperCase(),
      currently_with: pr.currently_with || '—',
      workflow_instance_id: pr.workflow_instance_id,
      _raw: pr,
    });
  });

  const sortedRows = sortRows(projectedRows, sortState, (row, key) => row[key]);

  // Expanded panels are interleaved AFTER sorting, so a re-sort can never
  // separate a row from the detail hanging beneath it.
  const tableRows = [];
  sortedRows.forEach((row) => {
    const pr = row._raw;
    const prId = row.id;
    tableRows.push(row);

    if (expandedRows[prId] && pr.items && pr.items.length > 0) {
      tableRows.push({
        _isExpandedContent: true,
        parentId: prId,
        content: renderExpandedContent(pr),
      });
    }
  });

  // 15 while PAID and BALANCE are shown — see the commented columns.
  const colSpanCount = 13;

  const ccWorkspaces = [
    {
      key: 'load',
      icon: '📥',
      label: 'WORKSPACE 1',
      title: 'Load Requisitions',
      color: '#0b318b',
      desc: 'Load requisitions awaiting Cost Control review. Review lines, adjust quantities, and forward.',
    },
    {
      key: 'reviewed',
      icon: '✅',
      label: 'WORKSPACE 2',
      title: 'Reviewed Requisitions',
      color: '#16a34a',
      desc: 'All requisitions successfully reviewed by Cost Control and forwarded onward. View only.',
    },
    {
      key: 'pending',
      icon: '⏳',
      label: 'WORKSPACE 3',
      title: 'Pending Consideration',
      color: '#d97706',
      // Returned only now that rejections have their own card — the old copy
      // said "returned or rejected", which was never quite true of this panel.
      desc: 'Requisitions returned by Cost Control — grouped by consideration type.',
    },
    {
      key: 'rejected',
      icon: '⛔',
      label: 'WORKSPACE 4',
      title: 'Rejected Requisitions',
      color: '#dc2626',
      desc: 'Requisitions rejected outright by Cost Control. View only.',
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        .req-table th, .req-table td { border: 1px solid black; }
        .btn-submit-beautified:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(11,49,139,0.4) !important; background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%) !important; }
        .btn-delete-beautified:hover { transform: translateY(-1px); background: #fee2e2 !important; border-color: #dc2626 !important; color: #dc2626 !important; }
        .btn-view-beautified:hover { transform: translateY(-1px); background: #dbeafe !important; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      {title && <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>{title}</h2>}

      {/* CC Queue workspace switcher */}
      {isCCQueue && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {ccWorkspaces.map((ws) => {
            const active = ccView === ws.key;
            return (
              <div
                key={ws.key}
                onClick={() => setCcView(ws.key)}
                // Hovering previews the selected look in the card's own colour.
                // Skipped while active — there is nothing to preview, and
                // restoring on mouse-leave would un-highlight the open card.
                onMouseEnter={(e) => {
                  if (active) return;
                  e.currentTarget.style.borderColor = ws.color;
                  e.currentTarget.style.background = `${ws.color}14`;
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                  if (active) return;
                  e.currentTarget.style.borderColor = `${ws.color}33`;
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                }}
                style={{
                  flex: '1 1 260px',
                  minWidth: 240,
                  cursor: 'pointer',
                  // Border WIDTH stays at 2px in every state and only the
                  // colour changes. Growing it on hover would resize the card
                  // by a pixel and make the whole row twitch as the pointer
                  // crosses it.
                  border: `2px solid ${active ? ws.color : `${ws.color}33`}`,
                  borderRadius: 12,
                  padding: 16,
                  background: active ? `${ws.color}14` : '#fff',
                  boxShadow: active ? '0 4px 12px rgba(0,0,0,0.08)' : 'var(--shadow-sm)',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: `${ws.color}22`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                      }}
                    >
                      {ws.icon}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.5 }}>
                      {ws.label}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#fff',
                      background: ws.color,
                      borderRadius: 10,
                      padding: '2px 9px',
                      minWidth: 20,
                      minHeight: 16,
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {ccCounts[ws.key] != null ? (
                      ccCounts[ws.key]
                    ) : (ws.key === 'pending' || ws.key === 'rejected') ? (
                      '—'
                    ) : (
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          border: '2px solid rgba(255,255,255,0.4)',
                          borderTopColor: '#fff',
                          borderRadius: '50%',
                          display: 'inline-block',
                          animation: 'spin 0.7s linear infinite',
                        }}
                      />
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: ws.color, margin: '10px 0 4px', textTransform: 'uppercase' }}>
                  {ws.title}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                  {ws.desc}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isCCQueue && !ccView ? (
        <div
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: 'var(--text-muted)',
            border: '2px dashed #767b82ff',
            borderRadius: 12,
          }}
        >
          👆 Select a workspace above to load its requisitions.
        </div>
      ) : (
        <>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, maxWidth: '30%', display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <input
              type="text"
              placeholder="Search by ref or title..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 12px',
                fontSize: 13,
                border: '1.5px solid #cbd5e1',
                borderRadius: 8,
                outline: 'none',
                background: 'var(--bg-input, #fff)',
                color: 'var(--text-primary, #1e293b)',
              }}
            />
          </div>
          <button
            onClick={triggerSearch}
            style={{
              padding: '8px 20px',
              background: '#0b318b',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 13,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Search
          </button>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
          {!draftsOnly && (
            <div style={{ width: 170, flexShrink: 0 }}>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'SUBMITTED', label: 'Under Review' },
                  { value: 'IN_PROGRESS', label: 'In Progress' },
                  { value: 'ON_HOLD', label: 'On Hold' },
                  { value: 'RETURNED', label: 'Returned' },
                  { value: 'REJECTED', label: 'Rejected' },
                  { value: 'DRAFT', label: 'Drafts' },
                ]}
              />
            </div>
          )}
          {!departmentId && (
            <div style={{ width: 170, flexShrink: 0 }}>
              <Select
                value={deptFilter}
                onChange={setDeptFilter}
                options={[{ value: '', label: 'All Departments' }, ...uniqueDepartments.map((d) => ({ value: d, label: d }))]}
              />
            </div>
          )}
          <div style={{ width: 170, flexShrink: 0 }}>
            <Select
              value={projectFilter}
              onChange={setProjectFilter}
              options={[{ value: '', label: 'All Projects' }, ...uniqueProjects.map((p) => ({ value: p, label: p }))]}
            />
          </div>
          {!draftsOnly && (
            <div style={{ width: 170, flexShrink: 0 }}>
              <Select
                value={requesterFilter}
                onChange={setRequesterFilter}
                options={[{ value: '', label: 'All Requesters' }, ...uniqueRequesters.map((r) => ({ value: r, label: r }))]}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Pending Consideration: grouped by why each request is parked ──
          Its own render rather than the shared table: the whole point of this
          workspace is the grouping, and a flat list with a status column would
          bury it. */}
      {isCCPendingView && (
        <Card style={{ borderTop: '1px solid black', borderLeft: '2.5px solid black', borderRight: '2.5px solid black', borderBottom: '2.5px solid black', borderRadius: 0, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, padding: '13px 18px', background: '#d97706', color: '#fff', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.06em' }}>
              ⏳ PENDING CONSIDERATION — GROUPED BY STATUS
            </span>
            <span style={{ fontSize: 12, fontWeight: 700 }}>
              {considerationGroups?.total || 0} records · ₦{fmt(considerationGroups?.total_value || 0)}
            </span>
          </div>

          {!considerationGroups?.groups?.length ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13.5, lineHeight: 1.7 }}>
              Nothing is parked right now.
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Requisitions appear here once a reviewer returns one with a consideration status.
              </div>
            </div>
          ) : considerationGroups.groups.map((g) => {
            const open = !!openGroups[g.status_name];
            return (
              <div key={g.status_name} style={{ borderTop: '1px solid #e2e8f0' }}>
                <button
                  onClick={() => setOpenGroups((o) => ({ ...o, [g.status_name]: !o[g.status_name] }))}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 18px', background: open ? '#fff7ed' : '#fff',
                    border: 'none', borderLeft: `4px solid ${GROUP_COLOR[g.color] || '#94a3b8'}`,
                    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 16 }}>{g.icon || '•'}</span>
                  <strong style={{ fontSize: 13.5, color: 'var(--text-primary)' }}>{g.status_name}</strong>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {g.count} requisition{g.count === 1 ? '' : 's'} · ₦{fmt(g.total_value)}
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
                </button>

                {open && (
                  <div style={{ overflowX: 'auto', borderTop: '1px solid #f1f5f9' }}>
                    <table className="req-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          {['REF', 'TYPE', 'REQUESTER', 'DEPARTMENT', 'PROJECT', 'PRIORITY', 'LINES', 'EST. AMOUNT', 'STATUS', 'CURRENTLY WITH', ''].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#475569',
                                // Project names run long ("Port Harcourt Industrial
                                // Warehouse Complex") and on one line they pushed
                                // every other column narrow. Capped and wrapped.
                                // Department and project names are the two
                                // long ones ("Human Resources & Adminstration",
                                // "Port Harcourt Industrial Warehouse Complex")
                                // and on one line they pushed every other
                                // column narrow. Capped and wrapped.
                                ...(h === 'PROJECT' || h === 'DEPARTMENT' ? { width: 150 } : {}),
                              }}
                            >{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {g.requests.map((r) => {
                          const rid = r.id || r.purchase_request_id;
                          // Mostly a tracking panel — but a request the
                          // Approval Queue sent BACK lands here sitting at the
                          // Cost Control step, and that one is theirs to work.
                          // Those rows expand into the full review grid; rows
                          // parked with someone else stay flat and view-only,
                          // because the server would refuse the action anyway.
                          const holder = r.status_view?.current_step_name || r.currently_with || '—';
                          const atCostControl = /cost control/i.test(
                            r.status_view?.current_step_name || r.currently_with || '');
                          const rowOpen = atCostControl && !!expandedRows[rid];
                          return (
                            <React.Fragment key={rid}>
                              <tr
                                onClick={atCostControl
                                  ? () => setExpandedRows((prev) => ({ ...prev, [rid]: !prev[rid] }))
                                  : undefined}
                                style={{
                                  cursor: atCostControl ? 'pointer' : 'default',
                                  background: rowOpen ? '#eff6ff' : undefined,
                                }}
                              >
                                <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: '#0b318bff' }}>
                                  {atCostControl && (
                                    <span style={{ marginRight: 6, fontSize: 10 }}>{rowOpen ? '▼' : '▶'}</span>
                                  )}
                                  {r.pr_number || '—'}
                                </td>
                                <td style={{ padding: '9px 12px', textAlign: 'center' }}>{r.request_type_name || '—'}</td>
                                <td style={{ padding: '9px 12px', textAlign: 'center' }}>{r.requested_by_name || '—'}</td>
                                <td style={{
                                  padding: '9px 12px', textAlign: 'center',
                                  width: 150, maxWidth: 150,
                                  whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35,
                                }}>{r.department_name || '—'}</td>
                                <td style={{
                                  padding: '9px 12px', textAlign: 'center',
                                  width: 150, maxWidth: 150,
                                  whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35,
                                }}>
                                  {/* The API returns this flat on the request
                                      and on each line; the nested `project`
                                      object read before does not exist in the
                                      response, so the column was always "—". */}
                                  {r.project_name || r.items?.[0]?.project_name || '—'}
                                </td>
                                <td style={{ padding: '9px 12px', textAlign: 'center', textTransform: 'capitalize' }}>{r.priority_status || '—'}</td>
                                <td style={{ padding: '9px 12px', textAlign: 'center' }}>{r.items?.length || 0}</td>
                                <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700 }}>₦{fmt(r.total_estimated_cost)}</td>
                                <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                  <span style={{
                                    padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                    background: getStatusBg(r.request_status), color: getStatusCol(r.request_status),
                                  }}>{(r.request_status || '').toUpperCase()}</span>
                                  <StatusReason reason={r.status_reason} />
                                </td>
                                <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                  {/* One style for every holder. Whether the
                                      row is actionable is already said by the
                                      chevron, the Review button and the
                                      "view only" tag — dimming the name as
                                      well made the same column look like two
                                      different kinds of value. */}
                                  <span style={{ fontSize: 11.5, color: '#b45309', fontWeight: 700 }}>
                                    {holder}
                                  </span>
                                  {/* Said only where it is true. Labelling an
                                      at-Cost-Control row "view only" told the
                                      reviewer they could not act on the one
                                      request in this panel that is theirs. */}
                                  {!atCostControl && (
                                    <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
                                      view only
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setViewingPrId(rid);
                                      // Without the instance id the detail
                                      // screen skips the tracking call, which
                                      // leaves it with no approval history AND
                                      // no current step — and with no current
                                      // step it renders no action bar, so the
                                      // request could be opened but not worked.
                                      setViewingInstanceId(r.workflow_instance_id);
                                    }}
                                    className="btn-view-beautified"
                                    style={{
                                      background: '#eff6ff', color: '#0b318bff', border: '1.5px solid #0b318bff',
                                      padding: '4px 12px', borderRadius: 20, fontSize: 11.5, fontWeight: 700,
                                      cursor: 'pointer', transition: 'all 0.2s ease-in-out',
                                    }}
                                  >{atCostControl ? 'Review' : 'View'}</button>
                                </td>
                              </tr>
                              {rowOpen && (
                                <tr>
                                  <td colSpan={11} style={{ padding: 0, background: '#f8fafc' }}>
                                    {renderExpandedContent(r)}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      )}

      {!isCCPendingView && (
      <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
        {filteredPrs.length} requisitions found
      </div>
      )}

      {!isCCPendingView && (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Card style={{ border: '1px solid black', borderBottom: 'none', borderRadius: 0, boxShadow: 'none', overflow: 'hidden', padding: 0 }}>
        <div ref={viewport.ref} style={{ overflowX: 'auto' }}>
          <table className="req-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, border: '1px solid black' }}>
            <thead>
              <tr style={{ background: '#0b318bff', color: '#fff' }}>
                <SortTh label="REF" sortKey="pr_number" state={sortState} style={{ width: 140 }} />
                <SortTh label="DATE" sortKey="date" state={sortState} />
                <SortTh label="REQUESTER" sortKey="requester" state={sortState} />
                <SortTh label="DEPARTMENT" sortKey="department" state={sortState} style={{ minWidth: 150 }} />
                <SortTh label="TYPE" sortKey="type" state={sortState} style={{ minWidth: 130, width: 150 }} />
                <SortTh label="PROJECT" sortKey="project" state={sortState} style={{ minWidth: 160 }} />
                <SortTh label="PRIORITY" sortKey="priority" state={sortState} />
                <SortTh label="LINES" sortKey="lines" state={sortState} />
                <SortTh label="EST. AMOUNT" sortKey="total_estimated_cost" state={sortState} />
                <SortTh label="APPROVED" sortKey="approved_amount" state={sortState} />
                {/* PAID and BALANCE hidden on request — nothing disburses
                    against a requisition yet, so both read ₦0.00 on every row.
                    Uncomment these and their two <td>s below to restore them. */}
                {/* <SortTh label="PAID" sortKey="paid_amount" state={sortState} /> */}
                {/* <SortTh label="BALANCE" sortKey="balance_amount" state={sortState} /> */}
                <SortTh label="STATUS" sortKey="request_status" state={sortState} />
                <SortTh label="CURRENTLY WITH" sortKey="currently_with" state={sortState} />
                {/* Icons, so 180px of labels goes back to the columns that
                    were wrapping — Department and Project, mostly. */}
                <SortTh label="ACTIONS" style={{ minWidth: 96, width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, idx) => {
                if (row._isExpandedContent) {
                  return (
                    <tr key={`expand-${row.parentId}`}>
                      <td colSpan={colSpanCount} style={{ padding: 0 }}>
                        <PinnedDetail width={viewport.width}>
                          {row.content}
                        </PinnedDetail>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={row.id}
                    onClick={() => toggleRow(row.id)}
                    style={{
                      background: expandedRows[row.id] ? '#dbeafe' : (idx % 2 === 0 ? '#fff' : '#f8fafc'),
                      cursor: 'pointer',
                      transition: 'background 0.15s ease',
                    }}
                  >
                    <td style={{ padding: '12px', textAlign: 'center', fontWeight: 700, color: '#0b318bff', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
                        <span style={{ fontSize: 10, color: '#64748b', lineHeight: 1 }}>
                          {expandedRows[row.id] ? '▼' : '▶'}
                        </span>
                        <span>{row.pr_number}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{fmtDate(row.date)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{row.requester}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{row.department}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{row.type}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{row.project}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {(() => {
                        const pStyle = getPriorityBadgeStyle(row.priority);
                        return (
                          <span
                            style={{
                              color: pStyle.color,
                              background: pStyle.bg,
                              border: `1px solid ${pStyle.border}`,
                              padding: '4px 10px',
                              borderRadius: 12,
                              fontSize: 11,
                              fontWeight: 700,
                              display: 'inline-block',
                              textTransform: 'capitalize',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.priority}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>{row.lines}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700 }}>₦{fmtNum(row.total_estimated_cost)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>₦{fmtNum(row.approved_amount)}</td>
                    {/* PAID and BALANCE — hidden, see the headers above. */}
                    {/* <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#3b82f6' }}>₦{fmtNum(row.paid_amount)}</td> */}
                    {/* <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#ea580c' }}>₦{fmtNum(row.balance_amount)}</td> */}
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <span
                        style={{
                          background: getStatusBg(row.request_status),
                          color: getStatusCol(row.request_status),
                          border: `1px solid ${getStatusCol(row.request_status)}40`,
                          padding: '4px 10px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 700,
                          display: 'inline-block',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getStatusLabel(row.request_status)}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{row.currently_with}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {row.request_status === 'DRAFT' ? (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                          <ActionIcon
                            title="View Requisition"
                            color="#0b318bff"
                            onClick={(e) => {
                              // The whole row toggles the expander, so this has
                              // to stop the bubble or View would expand it on
                              // the way out.
                              e.stopPropagation();
                              setViewingPrId(row.id);
                              setViewingInstanceId(row.workflow_instance_id);
                            }}
                          >
                            <EyeIcon />
                          </ActionIcon>
                          {!isCCQueue && (
                            <ActionIcon
                              title="Edit Draft"
                              color="#a16207"
                              onClick={(e) => { e.stopPropagation(); setEditingPrId(row.id); }}
                            >
                              <PencilIcon />
                            </ActionIcon>
                          )}
                          <ActionIcon
                            title="Submit for Approval"
                            color="#0b318bff"
                            onClick={(e) => { e.stopPropagation(); handleFetchApprovers(row.id); }}
                          >
                            <SendIcon />
                          </ActionIcon>
                          <ActionIcon
                            title="Delete Draft"
                            color="#ef4444"
                            onClick={(e) => { e.stopPropagation(); setDeletingPrId(row.id); setShowDeleteModal(true); }}
                          >
                            <TrashIcon />
                          </ActionIcon>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                          <ActionIcon
                            title="View Requisition"
                            color="#0b318bff"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingPrId(row.id);
                              setViewingInstanceId(row.workflow_instance_id);
                            }}
                          >
                            <EyeIcon />
                          </ActionIcon>

                          {/* returned_TO_step_order is what says where the
                              return landed: null means it came all the way back
                              to the requester, so they have something to
                              correct. A non-null value means it stopped at an
                              earlier APPROVER — it reads RETURNED but is not
                              the requester's to edit, so no pencil.
                              (returned_FROM_step_order is written on every
                              return and cannot tell the two apart.) */}
                          {!isCCQueue && row.request_status === 'RETURNED' && row.returned_to_step_order == null && (
                            <ActionIcon
                              title="Correct and Resubmit"
                              color="#a16207"
                              onClick={(e) => { e.stopPropagation(); setEditingPrId(row.id); }}
                            >
                              <PencilIcon />
                            </ActionIcon>
                          )}

                          {/* Same direct edit the Approved list carries, one
                              step earlier: MD/DMD correcting a figure or a
                              description in front of them, without bouncing the
                              request back down the chain for a typo. It edits
                              the record only — the workflow is untouched, which
                              the edit page states at the top.

                              ?from= so Back, Cancel and a save all return to
                              this queue rather than the approved list. */}
                          {isApprovalQueue && (
                            <ActionIcon
                              title="Edit Requisition"
                              color="#0b318bff"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/enterprise-requisition/edit/${row.id}?from=approval-queue`);
                              }}
                            >
                              <PencilIcon />
                            </ActionIcon>
                          )}

                          {/* Deletable only while it is still between the
                              requester and their own department head, and no
                              desk beyond that has ever acted on it — such a
                              request carries no Cost Control or Approval Queue
                              review to destroy.

                              Read off row._raw, not row: tableRows is a
                              hand-built projection for the table cells and
                              carries none of these fields, so anything derived
                              here saw undefined on every row. _raw is the
                              untouched API object it was built from.

                              can_delete, NOT isDepartmentBounce: the step
                              numbers cannot answer this on their own. A request
                              Cost Control sent back to the department head, who
                              sent it on back to the requester, ends up with
                              returned_from = the first step and returned_to =
                              null — indistinguishable from a clean bounce, yet
                              Cost Control has reviewed it. The server decides,
                              and the delete endpoint enforces the same rule. */}
                          {draftsOnly && (row._raw || row)?.can_delete && (
                            <ActionIcon
                              title="Delete Requisition"
                              color="#ef4444"
                              onClick={(e) => { e.stopPropagation(); setDeletingPrId(row.id); setShowDeleteModal(true); }}
                            >
                              <TrashIcon />
                            </ActionIcon>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {tableRows.length === 0 && (
                <tr>
                  <td colSpan={colSpanCount} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                    No requisitions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </Card>
      {/* Pagination sits outside the scroll container so it stays fixed while
          the table scrolls sideways. Its own border frames just this row. */}
      <div style={{ borderTop: '1px solid black', borderLeft: '2.5px solid black', borderRight: '2.5px solid black', borderBottom: '2.5px solid black', background: 'var(--bg-card)' }}>
        <Pagination
          total={totalCount}
          skip={page.skip}
          limit={page.limit}
          onChange={setPage}
        />
      </div>
      </div>
      )}
        </>
      )}

      {/* ── Rejection / Hold reason modal (per line) ── */}
      {rejectReasonModal && (() => {
        const isApprovalMode = rejectReasonModal.mode === 'approval';
        const isHold = rejectReasonModal.status === 'on_hold';
        const modalTitle = isHold ? 'Reason for Hold' : 'Rejection Reason';
        const currentValue = isApprovalMode
          ? (apprEdits[rejectReasonModal.prId]?.[rejectReasonModal.itemId]?.appr_reason || '')
          : (lineEdits[rejectReasonModal.prId]?.[rejectReasonModal.itemId]?.cc_rejected_reason || '');
        const handleReasonChange = (value) =>
          isApprovalMode
            ? updateApprEdit(rejectReasonModal.prId, rejectReasonModal.itemId, 'appr_reason', value)
            : updateLineEdit(rejectReasonModal.prId, rejectReasonModal.itemId, 'cc_rejected_reason', value);

        return (
          <Modal title={modalTitle} onClose={() => setRejectReasonModal(null)} width={400}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                {isHold ? 'Reason for putting this line on hold' : 'Reason for Rejection'} <span style={{ color: 'red' }}>*</span>
              </label>
              <textarea
                rows={3}
                value={currentValue}
                onChange={(e) => handleReasonChange(e.target.value)}
                placeholder="Enter reason..."
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  fontSize: 13,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Btn variant="outline" onClick={() => setRejectReasonModal(null)}>Cancel</Btn>
              <Btn
                style={{ background: isHold ? '#eab308' : '#ef4444', color: '#fff', border: 'none' }}
                onClick={() => setRejectReasonModal(null)}
              >
                Save Reason
              </Btn>
            </div>
          </Modal>
        );
      })()}

      {/* ── Workflow Action Modal (Approve / Return / Reject / Hold) ── */}
      {actionModal && (() => {
        const ACTION_META = {
          approve: { label: 'Approve', title: 'Approve Request', color: '#16a34a', verb: 'approve', commentRequired: false, commentPlaceholder: 'Optional comment...' },
          return: { label: 'Return', title: 'Return Request', color: '#f97316', verb: 'return', commentRequired: true, commentPlaceholder: 'Explain why this request is being returned...' },
          reject: { label: 'Reject', title: 'Reject Request', color: '#ef4444', verb: 'reject', commentRequired: true, commentPlaceholder: 'Explain why this request is being rejected...' },
          hold: { label: 'Hold', title: 'Put Request On Hold', color: '#eab308', verb: 'put on hold', commentRequired: true, commentPlaceholder: 'Explain why this request is being put on hold...' },
          // No comment required: lifting a hold is undoing something, not
          // deciding anything, so demanding a justification would be friction
          // with nothing on the other side of it.
          resume: { label: 'Resume', title: 'Lift Hold', color: '#16a34a', verb: 'take off hold', commentRequired: false, commentPlaceholder: 'Optional note...' },
        };
        const baseMeta = ACTION_META[actionModal.type] || ACTION_META.approve;
        // An approve on a request that was sent BACK is a resubmit — the same
        // wording the button that opened this dialog uses. Only the words
        // change; the action and its colour are unchanged.
        const actionPr = (prs || []).find(
          (x) => (x.id || x.purchase_request_id) === actionModal.prId);
        const isResubmit = actionModal.type === 'approve' && isAwaitingCorrection(actionPr);
        const meta = isResubmit
          ? { ...baseMeta, label: 'Resubmit', title: 'Resubmit Request', verb: 'resubmit' }
          : baseMeta;
        return (
        <Modal
          title={meta.title}
          onClose={() => setActionModal(null)}
        >
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 13, marginBottom: 16 }}>
              You are about to <strong>{meta.verb}</strong> this purchase request.
              {actionModal.type === 'reject' && ' This is final — the request will not be resubmittable.'}
              {actionModal.type === 'hold' && ' The request stays with you and you can act on it again later.'}
            </p>
            {actionModal.reviewed === false && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
                <span>⚠️</span>
                <span>
                  You did not review the line items.{' '}
                  {actionModal.type === 'approve' && 'All line items will automatically be marked ACCEPTED.'}
                  {actionModal.type === 'reject' && "All line items will automatically be marked REJECTED, using your comment below as each line's rejection reason."}
                  {actionModal.type === 'hold' && "All line items will automatically be marked ON HOLD, using your comment below as each line's hold reason."}
                  {actionModal.type === 'return' && 'You can still return this request without reviewing them.'}
                </span>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                Comment {meta.commentRequired && <span style={{ color: 'red' }}>*</span>}
              </label>
              <textarea
                rows={3}
                value={actionComment}
                onChange={(e) => setActionComment(e.target.value)}
                placeholder={meta.commentPlaceholder}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  fontSize: 13,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            {actionModal.type === 'return' && returnTargets.length > 1 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Return to <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                  title="Return To"
                  value={returnTarget}
                  onChange={setReturnTarget}
                  options={returnTargets.map((t) => ({
                    value: t.value,
                    label: t.is_default ? `${t.label} (default)` : t.label,
                  }))}
                />
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                  {returnTargets.find((t) => t.value === returnTarget)?.description
                    || 'Choose where this request goes back to.'}
                </div>
              </div>
            )}

            {actionModal.type === 'return' && considerationStatuses.length > 0
              && offersConsiderationStatus(returnFromStep, returnTargets, returnTarget) && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Consideration Status <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                  title="Consideration Status"
                  value={considerationStatusId}
                  onChange={setConsiderationStatusId}
                  placeholder="Choose a heading"
                  options={considerationOptionsFor(considerationStatuses, returnFromStep).map((c) => ({
                    value: c.id,
                    label: `${c.icon ? c.icon + ' ' : ''}${c.name}`,
                  }))}
                />
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                  Groups the request under this heading in Pending Consideration.
                </div>
              </div>
            )}

            {actionModal.type === 'approve' && nextStepInfo && nextStepInfo.approver_mode !== 'is_final' && nextStepInfo.step_name && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  {nextStepInfo.approver_mode === 'returning_step'
                    ? `Goes back to - ${nextStepInfo.step_name}`
                    : `Next Approval Step - ${nextStepInfo.step_name}`}
                </label>
                {nextStepInfo.approver_mode === 'returning_step' ? (
                  // The returning approver is already recorded on the step, so
                  // there is nobody to nominate — just say where it is going.
                  // Same component the inbox and the details page use, so all
                  // three describe the move identically.
                  <ReturnDestinationNote stepName={nextStepInfo.step_name} />
                ) : nextStepInfo.approver_mode === 'specific_user' ? (
                  <Select
                    value={nextApproverId}
                    onChange={setNextApproverId}
                    options={nextStepInfo.eligible_users.map((a) => ({
                      value: a.user_id || a.id,
                      label: `${a.name} (${a.email})`,
                    }))}
                  />
                ) : (
                  <Select
                    value="step_queue"
                    onChange={() => {}}
                    options={[{ value: 'step_queue', label: `🏢 ${nextStepInfo.step_name}` }]}
                  />
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="outline" onClick={() => setActionModal(null)} disabled={submittingAction}>Cancel</Btn>
            <Btn
              onClick={submitAction}
              disabled={submittingAction || (
                actionModal.type === 'return'
                && offersConsiderationStatus(returnFromStep, returnTargets, returnTarget)
                && !considerationStatusId
              )}
              style={{
                background: meta.color,
                color: '#fff',
                border: 'none',
              }}
            >
              {submittingAction ? 'Submitting...' : `Confirm ${meta.label}`}
            </Btn>
          </div>
        </Modal>
        );
      })()}

      {/* ── Preparing Submission spinner ── */}
      {loadingApprovers && (
        <Modal hideHeader width={360} onClose={() => {}}>
          <div style={{ padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                width: 40, height: 40,
                border: '4px solid #e2e8f0',
                borderTop: '4px solid #0b318bff',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0b318bff', marginBottom: 4 }}>Preparing Submission...</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>Fetching eligible approvers for workflow</div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirmation ── */}
      {showDeleteModal && (
        <Modal title="Delete Requisition" onClose={() => setShowDeleteModal(false)} width={420}>
          <div style={{ marginBottom: 20, fontSize: 14, color: '#334155', lineHeight: 1.5 }}>
            Are you sure you want to delete this draft requisition? This action cannot be undone.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Btn>
            <Btn style={{ background: '#ef4444', borderColor: '#ef4444', color: '#fff' }} onClick={handleDeletePR}>Delete</Btn>
          </div>
        </Modal>
      )}

      {/* ── Approver Selection Modal ── */}
      {showApproverModal && (
        <Modal title="Submit for Approval" onClose={submittingWorkflow ? () => { } : () => setShowApproverModal(false)}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
              First Approver{firstStepInfo?.step_name ? ` - ${firstStepInfo.step_name}` : ''}
            </label>
            {firstStepInfo?.approver_mode === 'specific_user' ? (
              <Select
                value={selectedApproverId}
                onChange={setSelectedApproverId}
                disabled={submittingWorkflow}
                options={approvers.map((a) => ({ value: a.user_id || a.id, label: `${a.name} (${a.email})` }))}
              />
            ) : (
              <Select
                value="step_queue"
                onChange={() => {}}
                options={[{ value: 'step_queue', label: `🏢 ${firstStepInfo?.step_name || 'Step Queue'}` }]}
              />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Btn variant="outline" disabled={submittingWorkflow} onClick={() => setShowApproverModal(false)}>Cancel</Btn>
            <Btn
              onClick={() => handleSubmit(submittingPrId, selectedApproverId)}
              loading={submittingWorkflow}
            >
              {submittingWorkflow ? 'Submitting…' : 'Submit'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* ── Submission Success ── */}
      {showSubmitSuccessModal && (
        <Modal hideHeader width={420} onClose={() => setShowSubmitSuccessModal(false)}>
          <div style={{ padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
              style={{
                width: 64, height: 64, borderRadius: '50%',
                background: '#dcfce7', color: '#16a34a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 16, boxShadow: '0 0 0 8px #f0fdf4',
              }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Requisition Submitted!</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
              {submittedPRNumber ? (
                <>Requisition <strong style={{ color: '#0b318bff' }}>{submittedPRNumber}</strong> has been successfully submitted to {firstStepInfo?.step_name || 'the first approval step'} for processing.</>
              ) : (
                <>Your purchase requisition has been successfully submitted for approval.</>
              )}
            </p>
            <button
              onClick={() => setShowSubmitSuccessModal(false)}
              style={{
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                color: '#fff', border: 'none', padding: '10px 32px',
                borderRadius: 20, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(22,163,74,0.3)', transition: 'all 0.2s ease-in-out',
              }}
              onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
              onMouseOut={(e) => (e.currentTarget.style.transform = 'none')}
            >
              Great, Thanks!
            </button>
          </div>
        </Modal>
      )}

      {/* ── Approve/Return/Reject/Hold Outcome Confirmation ── */}
      {outcomeModal && (() => {
        const OUTCOME_STYLE = {
          approve: { color: '#16a34a', bg: '#dcfce7', ring: '#f0fdf4', gradient: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', shadow: 'rgba(22,163,74,0.3)', icon: <polyline points="20 6 9 17 4 12" /> },
          return: { color: '#f97316', bg: '#ffedd5', ring: '#fff7ed', gradient: 'linear-gradient(135deg, #f97316 0%, #c2410c 100%)', shadow: 'rgba(249,115,22,0.3)', icon: (<><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>) },
          reject: { color: '#ef4444', bg: '#fee2e2', ring: '#fef2f2', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', shadow: 'rgba(239,68,68,0.3)', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
          hold: { color: '#eab308', bg: '#fef9c3', ring: '#fefce8', gradient: 'linear-gradient(135deg, #eab308 0%, #a16207 100%)', shadow: 'rgba(234,179,8,0.3)', icon: (<><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></>) },
          // Its own entry rather than falling through to the approve style —
          // the fallback would have shown a green tick, which reads as
          // "approved" for an action that only un-parks the request.
          resume: { color: '#16a34a', bg: '#dcfce7', ring: '#f0fdf4', gradient: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', shadow: 'rgba(22,163,74,0.3)', icon: <polygon points="5 3 19 12 5 21 5 3" /> },
        };
        const style = OUTCOME_STYLE[outcomeModal.type] || OUTCOME_STYLE.approve;
        const dismissOutcome = () => {
          setOutcomeModal(null);
          // Re-read from the server so the group totals, the workspace badges
          // and the row list all agree — the local prune above is only there
          // to keep the panel honest while the modal is open.
          api.clearApiCache();
          fetchPRs().catch(() => { });
          refreshCcCounts();
        };

        return (
        <Modal hideHeader width={420} onClose={dismissOutcome}>
          <div style={{ padding: '24px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
              style={{
                width: 64, height: 64, borderRadius: '50%',
                background: style.bg,
                color: style.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 16,
                boxShadow: `0 0 0 8px ${style.ring}`,
              }}
            >
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                {style.icon}
              </svg>
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{outcomeModal.title}</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>{outcomeModal.message}</p>
            <button
              onClick={dismissOutcome}
              style={{
                background: style.gradient,
                color: '#fff', border: 'none', padding: '10px 32px',
                borderRadius: 20, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                boxShadow: `0 4px 12px ${style.shadow}`,
                transition: 'all 0.2s ease-in-out',
              }}
              onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
              onMouseOut={(e) => (e.currentTarget.style.transform = 'none')}
            >
              Great, Thanks!
            </button>
          </div>
        </Modal>
        );
      })()}
    </div>
  );
}
